/* Admin: dashboard, learning %, invitations, rights matrix, function MD editor */
import React from 'react';
import { Icon, Avatar, EntityLink } from './primitives.jsx';
import { BUILD_SHA, BUILD_TIME } from './shell.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
// The onboarding manual is the canonical README at the repo root.
// Vite's ?raw suffix imports the file as a string at build time, so
// editing README.md auto-syncs the in-app view (no template-literal
// duplication, no backtick-escape drama).
import onboardingMd from '../../README.md?raw';
import { exportShellUrl } from '../lib/export-shell.ts';
import { publicOrigin } from '../lib/public-origin.ts';
import { useSubCrumbs } from './route-title-context.jsx';
import {
  listAdminUsers,
  listAdminInvitations,
  createAdminInvitations,
  revokeAdminInvitation,
  regenerateAdminInvitation,
  patchAdminUser,
  generateAdminUserIntro,
  getAdminStats,
  getAdminDigitalTwin,
  listAuditEntries,
  getQueueHealth,
  listAdminFunctions,
  getAdminFunction,
  createAdminFunction,
  patchAdminFunction,
  deleteAdminFunction,
  evaluateAdminFunction,
  addFunctionMember,
  removeFunctionMember,
  adminServices, adminCompanies,
  adminIndustries,
  adminClients,
  adminProjects,
  adminCreditors,
  adminStaffingCategories,
  uploadClientLogo,
  getOrgRepoTree,
  listAdminFeedback,
  decideAdminFeedback,
  saveAdminFeedbackOptimization,
  listProposedIntents,
  promoteIntentObservation,
  dismissIntentObservation,
  listAllFunctions,
  getAgenticReadinessTimeseries,
  listSuggestions,
  resolveSuggestion,
  dismissSuggestion,
  listAgents,
  patchAgent,
  deleteAgent,
  regenerateAgentImage,
} from '../api.js';

/* ── AgenticReadinessChart ─────────────────────────────────────────
 *
 * 200px-tall full-width line chart of the firm's daily averaged
 * agentic_pct + completeness_pct across all functions. One data point
 * per day, written by the cron worker on the first tick after 00:00
 * UTC. See migration 0054 + functions/_lib/functions.ts:captureReadinessSnapshotIfDue.
 *
 * Empty state ("no snapshots yet") shows on day-0 since the cron has
 * never had a chance to fire. The next 00:00-UTC tick fills it in.
 */
const AgenticReadinessChart = () => {
  const [series, setSeries] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    getAgenticReadinessTimeseries()
      .then(s => { if (!cancelled) setSeries(s || []); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div style={{padding:'var(--space-4)',background:'#FCE6E0',color:'#A20235',borderRadius:'var(--r-md)',fontSize:'var(--text-sm)',marginBottom:'var(--space-5)'}}>
        Couldn't load agentic-readiness timeseries: {error}
      </div>
    );
  }

  const data = (series || []).map(p => ({
    date: p.date.slice(5),                              // "MM-DD" for compact axis labels
    'Agentic':      Math.round(p.avgAgenticPct      * 10) / 10,
    'Completeness': Math.round(p.avgCompletenessPct * 10) / 10,
  }));

  const latest    = data[data.length - 1];
  const previous  = data[data.length - 2];
  const deltaAg   = latest && previous ? (latest.Agentic      - previous.Agentic).toFixed(1)      : null;
  const deltaCp   = latest && previous ? (latest.Completeness - previous.Completeness).toFixed(1) : null;

  return (
    <div style={{
      border:'1px solid var(--border)',
      borderRadius:'var(--r-lg)',
      background:'var(--bg)',
      padding:'var(--space-4) var(--space-5)',
      marginBottom:'var(--space-5)',
    }}>
      <div style={{display:'flex',alignItems:'baseline',gap:'var(--space-4)',marginBottom:'var(--space-2)',flexWrap:'wrap'}}>
        <div style={{fontFamily:'var(--font-display)',fontSize:'var(--text-lg)'}}>
          Agentic readiness — daily average
        </div>
        {latest && (
          <>
            <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
              Latest: <strong style={{color:'var(--fg)'}}>{latest.Agentic}%</strong> agentic
              {deltaAg !== null && <span style={{marginLeft:4,color: deltaAg >= 0 ? 'var(--support-green)' : '#A20235'}}>({deltaAg >= 0 ? '+' : ''}{deltaAg})</span>}
              {' · '}
              <strong style={{color:'var(--fg)'}}>{latest.Completeness}%</strong> completeness
              {deltaCp !== null && <span style={{marginLeft:4,color: deltaCp >= 0 ? 'var(--support-green)' : '#A20235'}}>({deltaCp >= 0 ? '+' : ''}{deltaCp})</span>}
            </span>
            <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginLeft:'auto'}}>
              {data.length} day{data.length === 1 ? '' : 's'} of history
            </span>
          </>
        )}
      </div>
      <div style={{height:160,width:'100%'}}>
        {data.length === 0 ? (
          <div style={{
            height:'100%',display:'flex',alignItems:'center',justifyContent:'center',
            fontSize:'var(--text-sm)',color:'var(--fg-muted)',fontStyle:'italic',
          }}>
            {series === null
              ? 'Loading…'
              : 'No snapshots yet. The cron writes one row per day at 00:00 UTC — come back tomorrow.'}
          </div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={data} margin={{top:5,right:8,left:-15,bottom:0}}>
              <CartesianGrid stroke="#E5DDD5" strokeDasharray="2 4"/>
              <XAxis dataKey="date" stroke="#93867D" tick={{fontSize:10}} interval="preserveStartEnd"/>
              <YAxis stroke="#93867D" domain={[0, 100]} tick={{fontSize:10}} tickFormatter={v => `${v}%`}/>
              <Tooltip
                contentStyle={{fontSize:12,border:'1px solid #E5DDD5',borderRadius:8,background:'#fff'}}
                formatter={v => `${v}%`}
              />
              <Legend wrapperStyle={{fontSize:11,paddingTop:0}} iconSize={10}/>
              <Line type="monotone" dataKey="Agentic"      stroke="#FF6428" strokeWidth={2} dot={{r:2}} activeDot={{r:4}}/>
              <Line type="monotone" dataKey="Completeness" stroke="#715AAE" strokeWidth={2} dot={{r:2}} activeDot={{r:4}}/>
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export const AdminView = ({ currentUser }) => {
  const [tab, setTab] = React.useState('dash');
  const tabs = [
    { id: 'dash', label: 'Dashboard' },
    { id: 'functions', label: 'Functions' },
    { id: 'creditors', label: 'Creditors' },
    { id: 'categories', label: 'Categories' },
    { id: 'suggestions', label: 'Suggestions' },
    { id: 'agents', label: 'Agents' },
    { id: 'agent-images', label: 'Agent images' },
    { id: 'ai-models', label: 'AI Models' },
    { id: 'settings', label: 'Settings' },
    { id: 'slide-system', label: 'Slide system', href: 'slide-sandbox' },
    { id: 'deck-slides', label: 'Decks & rules' },
    { id: 'feedback', label: 'Errors & Optimizations' },
    { id: 'audit', label: 'Trust & audit' },
  ];
  // Surface the active tab as a sub-crumb so the breadcrumb reads
  // "eOS / Admin / Admin dashboard / <Tab>".
  const activeTabLabel = tabs.find(t => t.id === tab)?.label;
  useSubCrumbs(activeTabLabel ? [{ label: activeTabLabel, href: null }] : []);
  return (
    <div className="page">
      <div className="page__head">
        <h1>Admin</h1>
        <div className="lede">Visible only to Admin & Global Managing Partner. Operate the firm's repo: who's in it, what they can do, and how fast it's learning.</div>
      </div>
      <div className="admin-layout">
        <nav className="admin-nav">
          {tabs.map(t => <div key={t.id} className={`admin-nav__item ${tab === t.id ? 'on' : ''}`} onClick={() => t.href ? (window.location.hash = t.href) : setTab(t.id)}>{t.label}</div>)}
        </nav>
        <div className="admin-content">
          {tab === 'dash' && <AdminDash/>}
          {tab === 'functions' && <AdminFunctions currentUser={currentUser}/>}
          {tab === 'creditors' && <AdminCreditors/>}
          {tab === 'categories' && <AdminCategories/>}
          {tab === 'suggestions' && <AdminSuggestions/>}
          {tab === 'agents' && <AdminAgents/>}
          {tab === 'agent-images' && <AdminAgentImages/>}
          {tab === 'ai-models' && <AdminAiModels/>}
          {tab === 'settings' && <AdminSettings/>}
          {tab === 'deck-slides' && <AdminDeckSlides/>}
          {tab === 'feedback' && <AdminFeedback/>}
          {tab === 'audit' && <AdminAudit/>}
        </div>
      </div>
    </div>
  );
};

/* Suggested catalog items — names members gave (via the Welcome / Profile
 * Advisor chat) that didn't match the catalog. Each row opens the normal
 * Add-entity dialog prefilled with the name; on create the new item is
 * linked back onto the suggester's profile and the row clears. */
const SUGGESTION_KIND_TO_ENTITY = {
  service: 'services', industry: 'industries', client: 'clients',
  organization: 'organizations', project: 'projects',
};
const SUGGESTION_KIND_LABEL = {
  service: 'Service', industry: 'Industry', client: 'Client',
  organization: 'Organization', project: 'Project',
};

const AdminSuggestions = () => {
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(() => {
    setError(null);
    listSuggestions().then(setRows).catch(e => { setRows([]); setError(e.message); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const onCreated = async (sug, item) => {
    const itemId = item?.id || item?._id;
    setBusyId(sug.id);
    try { if (itemId) await resolveSuggestion(sug.id, itemId); }
    catch (e) { setError(e.message); }
    finally { setBusyId(null); load(); }
  };
  const onDismiss = async (sug) => {
    setBusyId(sug.id);
    try { await dismissSuggestion(sug.id); }
    catch (e) { setError(e.message); }
    finally { setBusyId(null); load(); }
  };

  if (rows === null) return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading suggestions…</div>;

  return (
    <div>
      <div className="lede" style={{marginBottom:14}}>
        Catalog items members mentioned (via the Welcome / Profile Advisor chat) that aren't in the firm catalog yet.
        Create the real item — prefilled and editable — and it's linked straight onto the profile of whoever suggested it.
      </div>
      {error && <div style={{padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5,marginBottom:12}}>{error}</div>}
      {rows.length === 0 ? (
        <div style={{padding:'24px 0',color:'var(--fg-muted)',fontSize:13.5}}>No pending suggestions — every name members have given matched the catalog.</div>
      ) : (
        <table className="tbl" style={{width:'100%'}}>
          <thead>
            <tr>
              <th style={{width:120}}>Type</th>
              <th>Suggested name</th>
              <th>Suggested by</th>
              <th style={{width:300}}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.id}>
                <td>
                  <span style={{fontSize:10.5,padding:'2px 9px',borderRadius:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:'var(--bg-card)',color:'var(--fg-muted)'}}>
                    {SUGGESTION_KIND_LABEL[s.kind] || s.kind}
                  </span>
                </td>
                <td style={{fontWeight:500}}>{s.name}</td>
                <td style={{color:'var(--fg-muted)',fontSize:12.5}}>{s.suggestedByName || '—'}</td>
                <td>
                  <div style={{display:'flex',gap:8,justifyContent:'flex-end',alignItems:'center',flexWrap:'wrap'}}>
                    <AddEntityButton
                      kind={SUGGESTION_KIND_TO_ENTITY[s.kind]}
                      initialName={s.name}
                      label="Add to catalog"
                      style={{padding:'5px 12px',fontSize:12}}
                      onCreated={item => onCreated(s, item)}
                    />
                    <button type="button" className="btn-ghost" style={{padding:'5px 12px',fontSize:12}} disabled={busyId === s.id} onClick={() => onDismiss(s)}>
                      Dismiss
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ── AdminAgents ───────────────────────────────────────────────────
 * Manage the firm's PUBLIC and SYSTEM agents in one table:
 *   - switch an agent between Public (firm-wide, every user) and System
 *     (admin-only) status,
 *   - set a public agent's visibility (For all / Only for admin),
 *   - delete an agent and its entire run history.
 * Personal agents are NOT shown — users manage their own on the Agents
 * page. Built-in master watchers + system singletons are listed read-only
 * (their runtime is hardcoded + re-seeded on every read), so their
 * status / delete controls are disabled. */
const AGENT_STATUS_META = {
  public: { label: 'Public', color: '#1A6A2B', bg: '#DCF1E2' },
  system: { label: 'System', color: '#A20235', bg: '#FCE6E0' },
  master: { label: 'Built-in', color: '#3B2D8C', bg: '#ECE8FB' },
};

// Bucket an agent into the admin table's three status groups.
const adminAgentStatus = (a) =>
  a.kind === 'master' ? 'master' : a.kind === 'system' ? 'system' : 'public';

// Per-role visibility targeting for a public agent. The stored value is
// either 'all' (everyone), a comma role list ('user,eOS-Manager'), or the
// legacy 'admin' (admins only). Admins always see every agent regardless,
// so the Admin chip only matters when it's the lone selection.
const VIS_ROLE_OPTIONS = [
  ['all', 'For all'],
  ['user', 'Users'],
  ['eOS-Manager', 'eOS Manager'],
  ['admin', 'Admin'],
];
const parseVisibility = (v) => {
  if (!v || v === 'all') return new Set(['all']);
  return new Set(String(v).split(',').map(s => s.trim()).filter(Boolean));
};
// Multi-select chip group. Clicking "For all" clears role targeting;
// clicking a role toggles it (and drops "For all"). Selecting no role
// falls back to 'all' so an agent is never hidden from everyone.
const AgentVisibilityCell = ({ value, busy, onChange }) => {
  const sel = parseVisibility(value);
  const isAll = sel.has('all');
  const toggle = (role) => {
    if (busy) return;
    if (role === 'all') { onChange('all'); return; }
    const next = new Set(isAll ? [] : sel);
    if (next.has(role)) next.delete(role); else next.add(role);
    const roles = [...next].filter(r => r !== 'all');
    onChange(roles.length ? roles : 'all');
  };
  return (
    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
      {VIS_ROLE_OPTIONS.map(([role, label]) => {
        const active = role === 'all' ? isAll : (!isAll && sel.has(role));
        return (
          <span
            key={role}
            className={`tag ${active ? 'tag--orange' : ''}`}
            style={{cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, fontSize:11}}
            onClick={() => toggle(role)}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

const AdminAgents = () => {
  const [agents, setAgents] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(() => {
    setError(null);
    return listAgents()
      .then(res => setAgents(res.agents || []))
      .catch(e => { setAgents([]); setError(e.message); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Only public (custom/firm) + system + master agents belong here.
  // Personal agents stay on the Agents page. Public first, then system,
  // then built-in masters; alphabetical within each group.
  const rows = React.useMemo(() => {
    const order = { public: 0, system: 1, master: 2 };
    return (agents || [])
      .filter(a => (a.kind === 'custom' && a.scope === 'firm') || a.kind === 'system' || a.kind === 'master')
      .sort((a, b) => {
        const d = order[adminAgentStatus(a)] - order[adminAgentStatus(b)];
        return d !== 0 ? d : (a.targetLabel || '').localeCompare(b.targetLabel || '');
      });
  }, [agents]);

  const patch = async (a, body) => {
    setBusyId(a.id); setError(null);
    try { await patchAgent(a.id, body); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };
  const onDelete = async (a) => {
    if (!window.confirm(`Delete agent "${a.targetLabel}"? Removes the agent and its entire run history. This can't be undone.`)) return;
    setBusyId(a.id); setError(null);
    try { await deleteAgent(a.id); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  if (agents === null) return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading agents…</div>;

  return (
    <div>
      <div className="lede" style={{marginBottom:14}}>
        The firm's <strong>public</strong> and <strong>system</strong> agents. Switch an agent between public and system,
        set who can see + run a public agent, or delete it. Personal agents are managed by their owners on the Agents page.
        Built-in master watchers + system singletons are read-only — their runtime is hardcoded.
      </div>
      {error && <div style={{padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5,marginBottom:12}}>{error}</div>}
      {rows.length === 0 ? (
        <div style={{padding:'24px 0',color:'var(--fg-muted)',fontSize:13.5}}>No public or system agents yet.</div>
      ) : (
        <table className="tbl" style={{width:'100%'}}>
          <thead>
            <tr>
              <th>Agent</th>
              <th style={{width:160}}>Status</th>
              <th style={{width:220}}>Visibility</th>
              <th style={{width:110}}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a => {
              const status = adminAgentStatus(a);
              const meta = AGENT_STATUS_META[status];
              const builtin = !!a.builtin;
              const busy = busyId === a.id;
              return (
                <tr key={a.id}>
                  <td>
                    <div style={{fontWeight:500}}>{a.targetLabel}</div>
                    {a.description && (
                      <div style={{color:'var(--fg-muted)',fontSize:11.5,marginTop:2,lineHeight:1.45,maxWidth:560,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
                        {a.description}
                      </div>
                    )}
                  </td>
                  <td>
                    {builtin ? (
                      <span style={{fontSize:10.5,padding:'2px 9px',borderRadius:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:meta.bg,color:meta.color}}>
                        {status === 'master' ? 'Built-in' : 'System · built-in'}
                      </span>
                    ) : (
                      <select className="select" style={{padding:'4px 8px',fontSize:12,minHeight:32}}
                        value={status === 'system' ? 'system' : 'public'} disabled={busy}
                        onChange={e => patch(a, { status: e.target.value })}>
                        <option value="public">Public</option>
                        <option value="system">System</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {status === 'public' && !builtin ? (
                      <AgentVisibilityCell
                        value={a.visibility}
                        busy={busy}
                        onChange={v => patch(a, { visibility: v })}
                      />
                    ) : (
                      <span style={{fontSize:12,color:'var(--fg-muted)'}}>
                        {status === 'master' ? 'For all users (firm-wide watcher)' : 'Only for admin'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{display:'flex',justifyContent:'flex-end'}}>
                      <button type="button" className="btn-ghost"
                        style={{padding:'5px 12px',fontSize:12,color: builtin ? 'var(--fg-muted)' : '#A20235'}}
                        disabled={busy || builtin}
                        title={builtin ? "Built-in agents can't be deleted" : 'Delete this agent'}
                        onClick={() => onDelete(a)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ── AdminCreditors ────────────────────────────────────────────────
 * Manage the firm's master creditor list (the billing legal entities a
 * project bills against). The list is governance content stored in
 * OrgRepo at /governance/creditors.md; this UI is the add/remove front
 * end. A creditor can only be deleted when no project still uses it —
 * the server returns the offending projects, which we surface inline so
 * the admin can reassign them first. */
const AdminCreditors = () => {
  const [creditors, setCreditors] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [newName, setNewName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // name → { message, projects } when a delete was blocked.
  const [blocked, setBlocked] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;
    adminCreditors.list()
      .then(list => { if (!cancelled) setCreditors(list); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true); setError(null);
    try {
      const list = await adminCreditors.add(name);
      setCreditors(list);
      setNewName('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async name => {
    if (busy) return;
    if (!window.confirm(`Delete creditor "${name}"? This removes it from the master list.`)) return;
    setBusy(true); setError(null);
    setBlocked(b => { const { [name]: _drop, ...rest } = b; return rest; });
    try {
      const list = await adminCreditors.remove(name);
      setCreditors(list);
    } catch (err) {
      if (err.projects && err.projects.length) {
        setBlocked(b => ({ ...b, [name]: { message: err.message, projects: err.projects } }));
      } else {
        setError(err.message);
      }
    } finally { setBusy(false); }
  };

  if (error && !creditors) {
    return <div style={{padding:'var(--space-4)',background:'#FCE6E0',color:'#A20235',borderRadius:'var(--r-md)',fontSize:'var(--text-sm)'}}>Couldn't load creditors: {error}</div>;
  }
  if (!creditors) return <div style={{color:'var(--fg-muted)',fontSize:13,padding:'12px 0'}}>Loading creditors…</div>;

  return (
    <div>
      <div className="lede" style={{marginBottom:16}}>
        The billing legal entities a project bills against. Stored in OrgRepo governance at <code>/governance/creditors.md</code>.
        Projects pick one creditor; organizations hold several (Organizations → Creditors). A creditor can only be deleted when no project still uses it.
      </div>

      <div style={{display:'flex',gap:8,marginBottom:18,maxWidth:520}}>
        <input
          className="input"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="New creditor — exact legal-entity name"
          maxLength={200}
          style={{flex:1}}
        />
        <button className="btn-orange" onClick={add} disabled={busy || !newName.trim()}>Add creditor</button>
      </div>

      {error && <div style={{padding:8,marginBottom:12,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{error}</div>}

      {creditors.length === 0 ? (
        <div style={{color:'var(--fg-muted)',fontSize:13,padding:'12px 0'}}>No creditors yet. Add the firm's billing entities above.</div>
      ) : (
        <table className="tbl" style={{width:'100%',maxWidth:640}}>
          <thead><tr><th>Creditor</th><th style={{width:120,textAlign:'right'}}>Actions</th></tr></thead>
          <tbody>
            {creditors.map(name => (
              <React.Fragment key={name}>
                <tr>
                  <td>{name}</td>
                  <td style={{textAlign:'right'}}>
                    <button className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5,color:'#A20235'}} onClick={() => remove(name)} disabled={busy}>Delete</button>
                  </td>
                </tr>
                {blocked[name] && (
                  <tr>
                    <td colSpan={2} style={{background:'#FCE6E0',color:'#A20235',fontSize:12,padding:'8px 12px'}}>
                      {blocked[name].message}
                      <div style={{marginTop:6,display:'flex',flexWrap:'wrap',gap:6}}>
                        {blocked[name].projects.map(p => (
                          <a key={p.id} className="tag" href={`#projects/${p.slug || p.id}`} style={{cursor:'pointer'}}>{p.name}</a>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ── AdminCategories ───────────────────────────────────────────────
 * The single source of truth for the non-project activity types used by
 * BOTH the weekly staffing plan and the daily booking grid (Internal,
 * Sales, Recruiting, Vacation, …). Built-ins can be renamed/recolored/
 * reordered/archived but not deleted; custom ones are fully removable
 * (unless still referenced by a plan/booking row — archive instead).
 * "Client-linked" types (e.g. Sales) attribute booked hours to a client. */
const AdminCategories = () => {
  const [cats, setCats] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState('');
  const [newColor, setNewColor] = React.useState('#4C8DBE');
  const [newClientLinked, setNewClientLinked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    adminStaffingCategories.list()
      .then(list => { if (!cancelled) setCats(list); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { setCats(await fn()); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const add = async () => {
    const label = newLabel.trim();
    if (!label || busy) return;
    await run(async () => {
      const list = await adminStaffingCategories.create({ label, color: newColor, clientLinked: newClientLinked });
      setNewLabel(''); setNewColor('#4C8DBE'); setNewClientLinked(false);
      return list;
    });
  };

  const patch = (slug, body) => run(() => adminStaffingCategories.patch(slug, body));
  const remove = (c) => {
    if (!window.confirm(`Delete category "${c.label}"? This removes it from staffing + booking.`)) return;
    run(() => adminStaffingCategories.remove(c.slug));
  };
  const move = (idx, dir) => {
    if (!cats) return;
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const a = cats[idx], b = cats[j];
    run(async () => {
      await adminStaffingCategories.patch(a.slug, { sortOrder: b.sortOrder });
      return adminStaffingCategories.patch(b.slug, { sortOrder: a.sortOrder });
    });
  };

  if (error && !cats) {
    return <div style={{padding:'var(--space-4)',background:'#FCE6E0',color:'#A20235',borderRadius:'var(--r-md)',fontSize:'var(--text-sm)'}}>Couldn't load categories: {error}</div>;
  }
  if (!cats) return <div style={{color:'var(--fg-muted)',fontSize:13,padding:'12px 0'}}>Loading categories…</div>;

  return (
    <div>
      <div className="lede" style={{marginBottom:16}}>
        Activity types shared by the staffing plan and the daily booking grid — everything <em>besides</em> client projects.
        This is the one list both screens read. <strong>Client-linked</strong> types (e.g. Sales) attribute booked hours to a client.
        Built-ins can be renamed, recolored, reordered and archived but not deleted.
      </div>

      <div style={{display:'flex',gap:8,marginBottom:18,flexWrap:'wrap',alignItems:'center'}}>
        <input
          className="input"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="New category — e.g. Marketing"
          maxLength={80}
          style={{flex:'1 1 220px'}}
        />
        <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} title="Colour" style={{width:38,height:32,padding:0,border:'1px solid var(--border)',borderRadius:6,background:'none',cursor:'pointer'}}/>
        <label style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,color:'var(--fg-muted)'}}>
          <input type="checkbox" checked={newClientLinked} onChange={e => setNewClientLinked(e.target.checked)}/>
          Client-linked
        </label>
        <button className="btn-orange" onClick={add} disabled={busy || !newLabel.trim()}>Add category</button>
      </div>

      {error && <div style={{padding:8,marginBottom:12,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{error}</div>}

      <table className="tbl" style={{width:'100%',maxWidth:760}}>
        <thead>
          <tr>
            <th style={{width:64}}>Order</th>
            <th style={{width:44}}>Colour</th>
            <th>Label</th>
            <th style={{width:110,textAlign:'center'}}>Client-linked</th>
            <th style={{width:90,textAlign:'center'}}>Active</th>
            <th style={{width:110,textAlign:'right'}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {cats.map((c, idx) => (
            <tr key={c.slug} style={{opacity: c.active ? 1 : 0.5}}>
              <td style={{whiteSpace:'nowrap'}}>
                <button className="btn-ghost" style={{padding:'1px 7px',fontSize:13}} onClick={() => move(idx, -1)} disabled={busy || idx === 0} title="Move up">↑</button>
                <button className="btn-ghost" style={{padding:'1px 7px',fontSize:13}} onClick={() => move(idx, 1)} disabled={busy || idx === cats.length - 1} title="Move down">↓</button>
              </td>
              <td>
                <input type="color" value={c.color} disabled={busy}
                  onChange={e => patch(c.slug, { color: e.target.value })}
                  title="Colour" style={{width:32,height:28,padding:0,border:'1px solid var(--border)',borderRadius:6,background:'none',cursor:'pointer'}}/>
              </td>
              <td>
                <input className="input" defaultValue={c.label} maxLength={80} disabled={busy}
                  style={{padding:'4px 8px',fontSize:13,maxWidth:260}}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  onBlur={e => { const v = e.target.value.trim(); if (v && v !== c.label) patch(c.slug, { label: v }); else e.target.value = c.label; }}/>
                {c.builtin && <span className="tag" style={{marginLeft:8,fontSize:10}}>built-in</span>}
                <div style={{fontSize:10.5,color:'var(--fg-muted)',marginTop:2}}><code>{c.slug}</code></div>
              </td>
              <td style={{textAlign:'center'}}>
                <input type="checkbox" checked={c.clientLinked} disabled={busy}
                  onChange={e => patch(c.slug, { clientLinked: e.target.checked })} title="Booking attributes hours to a client"/>
              </td>
              <td style={{textAlign:'center'}}>
                <input type="checkbox" checked={c.active} disabled={busy}
                  onChange={e => patch(c.slug, { active: e.target.checked })} title="Offered in the staffing + booking pickers"/>
              </td>
              <td style={{textAlign:'right'}}>
                {!c.builtin && (
                  <button className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5,color:'#A20235'}} onClick={() => remove(c)} disabled={busy}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminDash = () => {
  const [stats, setStats] = React.useState(null);
  const [twin, setTwin] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    getAdminStats().then(setStats).catch(err => setError(err.message));
    getAdminDigitalTwin().then(setTwin).catch(err => setError(err.message));
  }, []);

  const kpi = (label, value, sub, hint) => (
    <div className="kpi" title={hint || undefined}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{value}</div>
      <div className="kpi__sub">{sub}</div>
    </div>
  );

  const loading = stats === null && !error;
  const dash = stats || { users: {}, invitations: {}, chats: {}, workers: {}, tokens24h: {} };
  const u = dash.users || {};
  const inv = dash.invitations || {};
  const totalInvites = (inv.pending||0) + (inv.redeemed||0) + (inv.expired||0) + (inv.revoked||0);
  const workers = dash.workers || {};
  const tokens24h = dash.tokens24h || { input: 0, output: 0, cacheCreate: 0, cacheRead: 0, calls: 0 };
  const totalTokens24h = (tokens24h.input || 0) + (tokens24h.output || 0) + (tokens24h.cacheCreate || 0) + (tokens24h.cacheRead || 0);
  const cacheTokens24h = (tokens24h.cacheCreate || 0) + (tokens24h.cacheRead || 0);

  const fmtTokens = n => {
    if (!Number.isFinite(n)) return '—';
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
    return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 2 : 1)}M`;
  };

  return (
  <div>
    {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
    <div className="tile-grid">
      {kpi(
        'Active users',
        loading ? '…' : (u.total ?? 0),
        u.admins != null ? `${u.admins} admin${u.admins === 1 ? '' : 's'}${u.joinedLast7Days ? ` · +${u.joinedLast7Days} this week` : ''}` : '',
      )}
      {kpi(
        'Pending invites',
        loading ? '…' : (inv.pending ?? 0),
        totalInvites ? `of ${totalInvites} issued · ${inv.redeemed||0} redeemed` : 'none issued yet',
      )}
      {kpi(
        'Workers running',
        loading ? '…' : (workers.active ?? 0),
        workers.active ? 'queued or in-flight' : 'all idle',
      )}
      {kpi(
        'Tokens · 24h (firm-wide)',
        loading ? '…' : fmtTokens(totalTokens24h),
        tokens24h.calls
          ? `${tokens24h.calls} call${tokens24h.calls === 1 ? '' : 's'} · ${fmtTokens(tokens24h.input || 0)} in / ${fmtTokens(tokens24h.output || 0)} out${cacheTokens24h ? ` / ${fmtTokens(cacheTokens24h)} cache` : ''}`
          : 'no Claude calls in the last 24h',
      )}
    </div>

    <div className="section">
      <div className="section__head">
        <h2>User activity</h2>
        <span className="lede">Each registered user with their primary role, visit history, and token usage. Open a user's log for the full activity trail.</span>
      </div>
      {twin === null && !error && <div style={{padding:14,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}
      {twin && twin.length === 0 && (
        <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)'}}>
          No registered users yet. Invite people from Admin → People.
        </div>
      )}
      {twin && twin.length > 0 && (
        <table className="tbl">
          <thead><tr>
            <th>Person</th>
            <th>Role</th>
            <th style={{width:150}}>Last active</th>
            <th style={{width:90}} title="Platform openings — counted each time the person becomes active after 30+ minutes away">Visits</th>
            <th style={{width:120}}>Tokens used</th>
            <th style={{width:80}}></th>
          </tr></thead>
          <tbody>
            {twin.map(p => (
              <tr key={p.userId}>
                <td style={{fontWeight:500}}><EntityLink type="person" handle={p.userId}>{p.name}</EntityLink></td>
                <td style={{color:'var(--fg-muted)'}}>
                  {p.functionName ? p.functionName : <em>— no role assigned —</em>}
                </td>
                <td style={{color: p.lastActiveAt ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.lastActiveAt ? relTime(p.lastActiveAt) : 'never'}</td>
                <td>{p.visitCount || 0}</td>
                <td style={{fontFamily:'JetBrains Mono',fontSize:12.5}}>{fmtTokens(p.tokensUsed || 0)}</td>
                <td><a href={`#people/${p.userId}`} style={{color:'var(--orange)',fontSize:12.5}}>Log →</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
  );
};

export const StatusPill = ({ status }) => {
  const palette = {
    pending:  { bg: '#DCF1E2', fg: '#00702A' },
    redeemed: { bg: 'var(--bg-card)', fg: 'var(--fg)' },
    expired:  { bg: '#FFE7DC', fg: '#B83C0F' },
    revoked:  { bg: '#FCE6E0', fg: '#A20235' },
  }[status] || { bg: 'var(--bg-card)', fg: 'var(--fg)' };
  return <span style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:palette.bg,color:palette.fg}}>{status}</span>;
};

// `pastOnly` is for timestamps the SERVER stamped in the past (last seen,
// audit times, issued-at): a browser clock a minute behind the server
// would otherwise render them as a nonsense future ("in 2m"). Genuinely
// future-valued fields (invite expiry) must NOT pass it.
export function relativeTime(value, { pastOnly = false } = {}) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  let diffMs = d.getTime() - Date.now();
  if (pastOnly && diffMs > 0) diffMs = 0;
  const abs = Math.abs(diffMs);
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  const future = diffMs > 0;
  const fmt = (n, unit) => `${future ? 'in ' : ''}${n}${unit}${future ? '' : ' ago'}`;
  if (abs < hr) return fmt(Math.max(1, Math.round(abs / min)), 'm');
  if (abs < day) return fmt(Math.round(abs / hr), 'h');
  if (abs < 30 * day) return fmt(Math.round(abs / day), 'd');
  return d.toISOString().slice(0, 10);
}

export const InviteModal = ({ onClose }) => {
  const [emailsText, setEmailsText] = React.useState('');
  const [role, setRole] = React.useState('user');
  // Function (= firm role, e.g. CFO / Managing Partner). Required.
  // Loaded once from /api/functions; the admin picks from a dropdown
  // so the new user can't free-text their own role at register time.
  // `loaded` distinguishes "still fetching" from "fetched and empty"
  // so the UI can be honest about which state it's in.
  const [functions, setFunctions] = React.useState([]);
  const [functionsLoaded, setFunctionsLoaded] = React.useState(false);
  const [functionId, setFunctionId] = React.useState('');
  const [functionsError, setFunctionsError] = React.useState(null);
  const [note, setNote] = React.useState('Welcome to the team. Set up your profile so we can route you the right work.');
  const [expiresInDays, setExpiresInDays] = React.useState(14);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [issued, setIssued] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    listAllFunctions()
      .then(fns => {
        if (cancelled) return;
        const sorted = (fns || []).slice().sort((a, b) => a.name.localeCompare(b.name));
        setFunctions(sorted);
        setFunctionsLoaded(true);
        if (sorted.length > 0 && !functionId) setFunctionId(sorted[0].id);
      })
      .catch(err => {
        if (cancelled) return;
        setFunctionsError(err.message);
        setFunctionsLoaded(true);
      });
    return () => { cancelled = true; };
    // functionId is intentionally omitted from deps — initial seed only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsedEmails = emailsText
    .split(/[,;\s]+/)
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
  const emailCount = parsedEmails.length;

  const submit = async e => {
    e.preventDefault();
    setError(null);
    if (!functionId) {
      setError('Pick a role from the dropdown before issuing invites.');
      return;
    }
    setBusy(true);
    try {
      const result = await createAdminInvitations({
        emails: parsedEmails,
        role,
        functionId,
        note: note || undefined,
        expiresInDays,
      });
      setIssued(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:issued ? 640 : 800,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>Invite</div>

        {!issued ? (
          <form onSubmit={submit}>
            <h3>Add people to the firm.</h3>
            <p style={{color:'var(--fg-muted)',fontSize:14,marginBottom:20,marginTop:6,maxWidth:'66ch'}}>
              Each email gets its own invite link. Leave the list empty to issue one open link anyone can use once.
            </p>

            <div className="intake-cols">
              <div className="intake-col">
                <div className="field" style={{marginBottom:0}}>
                  <label>Email(s) — comma or newline separated</label>
                  <textarea
                    className="textarea"
                    rows={4}
                    value={emailsText}
                    placeholder="alex@firm.com, sam@firm.com"
                    onChange={e => setEmailsText(e.target.value)}
                  />
                  <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
                    {emailCount === 0 ? '1 open invite link will be issued' : `${emailCount} invite link${emailCount === 1 ? '' : 's'} will be issued`}
                  </div>
                </div>

                <div className="field" style={{marginBottom:0}}>
                  <label>Personal note</label>
                  <textarea className="textarea" rows={4} value={note} onChange={e => setNote(e.target.value)}/>
                </div>
              </div>

              <div className="intake-col">
                <div className="field respo-2col" style={{gap:10,marginBottom:0}}>
                  <div>
                    <label>Admin-Role</label>
                    <select className="select" value={role} onChange={e => setRole(e.target.value)}>
                      <option value="user">User</option>
                      <option value="eOS-Manager">eOS-Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label>Expires in (days)</label>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={90}
                      value={expiresInDays}
                      onChange={e => setExpiresInDays(parseInt(e.target.value, 10) || 14)}
                    />
                  </div>
                </div>

                <div className="field" style={{marginBottom:0}}>
                  <label>Role / function *</label>
                  <select
                    className="select"
                    value={functionId}
                    onChange={e => setFunctionId(e.target.value)}
                    required
                  >
                    {!functionsLoaded && <option value="">Loading functions…</option>}
                    {functionsLoaded && functions.length === 0 && <option value="">No functions configured</option>}
                    {functions.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  {functionsError && (
                    <div style={{fontSize:'var(--text-xs)',color:'#A20235',marginTop:'var(--space-1)'}}>
                      Could not load roles: {functionsError}
                    </div>
                  )}
                  {functionsLoaded && !functionsError && functions.length === 0 && (
                    <div style={{fontSize:'var(--text-xs)',color:'#A20235',marginTop:'var(--space-1)'}}>
                      No roles in the firm catalogue yet. Go to <strong>Admin → Roles → Add function</strong> first, then come back.
                    </div>
                  )}
                  <div style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginTop:'var(--space-1)'}}>
                    The invitee will be locked into this role on signup. Manage the list in Admin → Roles.
                  </div>
                </div>
              </div>
            </div>

            <div style={{padding:'10px 14px',background:'var(--bg-alt)',borderRadius:10,fontSize:12.5,color:'var(--fg-muted)',margin:'16px 0 0'}}>
              <strong style={{color:'var(--fg)'}}>How it works:</strong> we issue a unique link per email; share it with the invitee.
              They set their own password on first sign-in. Links expire after the chosen window or when revoked.
            </div>

            {error && <div style={{marginTop:14,padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-orange" disabled={busy}>
                {busy ? 'Issuing…' : `Issue ${emailCount === 0 ? '1 open' : emailCount} invite${emailCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </form>
        ) : (
          <IssuedInvitesPanel issued={issued} onClose={onClose}/>
        )}
      </div>
    </div>
  );
};

export const IssuedInvitesPanel = ({ issued, onClose }) => {
  // Build invite URLs against the canonical brand origin (default
  // erasphere.eu) rather than wherever the admin happens to be
  // browsing. Avoids inviting people to the Pages preview URL when
  // an admin opens the panel from a preview deploy.
  const origin = publicOrigin();
  const linkFor = token => `${origin}/?invite=${encodeURIComponent(token)}`;
  const [copiedIdx, setCopiedIdx] = React.useState(null);
  const copy = (text, idx) => {
    try { navigator.clipboard.writeText(text); } catch { /* clipboard blocked */ }
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  return (
    <div>
      <h3>{issued.length} invitation{issued.length === 1 ? '' : 's'} issued.</h3>
      <p style={{color:'var(--fg-muted)',fontSize:14,marginBottom:20,marginTop:6}}>
        Copy and share each link directly. These tokens are shown <strong>once</strong> — we don't store them in clear.
      </p>

      <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:24}}>
        {issued.map((i, idx) => {
          const url = linkFor(i.token);
          return (
            <div key={i.id} style={{padding:12,background:'var(--bg-alt)',borderRadius:10,display:'flex',gap:10,alignItems:'center'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>{i.email || 'Open invite'}</div>
                <code style={{fontFamily:'JetBrains Mono',fontSize:11,color:'var(--fg-muted)',display:'block',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{url}</code>
              </div>
              <button className="btn-ghost" style={{padding:'6px 12px',fontSize:12}} onClick={() => copy(url, idx)}>
                {copiedIdx === idx ? 'Copied' : 'Copy link'}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button className="btn-orange" onClick={onClose}>Done</button>
      </div>
    </div>
  );
};

const AdminFunctions = ({ currentUser }) => {
  const [functions, setFunctions] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [activeId, setActiveId] = React.useState(null);
  const [detail, setDetail] = React.useState(null);  // { function, members }
  const [error, setError] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  React.useEffect(() => {
    listAdminUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  const reloadList = React.useCallback(async () => {
    setError(null);
    try {
      const list = await listAdminFunctions();
      setFunctions(list);
      if (list.length > 0 && !list.find(f => f.id === activeId)) setActiveId(list[0].id);
    } catch (err) {
      setError(err.message);
    }
  }, [activeId]);

  React.useEffect(() => { reloadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!activeId) { setDetail(null); return; }
    let active = true;
    getAdminFunction(activeId)
      .then(d => active && setDetail(d))
      .catch(err => active && setError(err.message));
    return () => { active = false; };
  }, [activeId]);

  const sortedFunctions = React.useMemo(
    () => [...(functions || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [functions],
  );

  return (
    <div>
      <AgenticReadinessChart/>
      <div className="respo-rail">
      <div>
        <button className="btn-orange" style={{width:'100%',marginBottom:12}} onClick={() => setCreateOpen(true)}>
          <Icon name="plus" size={14}/> Add function
        </button>

        {error && <div style={{padding:10,marginBottom:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12}}>{error}</div>}

        {functions === null && <div style={{padding:14,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}

        {functions && functions.length === 0 && (
          <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)'}}>
            No functions yet. Add the first one to start mapping the firm.
          </div>
        )}

        <div className="repo-tree">
          {sortedFunctions.map(f => {
            const pct = Math.max(0, Math.min(100, f.agenticPct ?? 0));
            return (
              <div key={f.id} className={`row ${activeId === f.id ? 'on' : ''}`} onClick={() => setActiveId(f.id)}>
                <Icon name="file" size={12}/>
                <span style={{flex:1,minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{f.name}</span>
                <span
                  style={{display:'inline-flex',alignItems:'center',gap:6,flexShrink:0}}
                  title={`Agentic ${pct}% · ${f.memberCount} member${f.memberCount === 1 ? '' : 's'}`}
                >
                  <span style={{position:'relative',width:54,height:5,background:'var(--bg-alt)',borderRadius:3,overflow:'hidden'}}>
                    <span style={{position:'absolute',left:0,top:0,bottom:0,width:`${pct}%`,background:'#715AAE',borderRadius:3}}/>
                  </span>
                  <span style={{fontSize:10.5,fontFamily:'JetBrains Mono',color:'var(--fg-muted)',minWidth:30,textAlign:'right'}}>
                    {pct}%
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {detail
          ? <FunctionEditor
              key={detail.function.id}
              detail={detail}
              users={users || []}
              currentUser={currentUser}
              onReload={() => Promise.all([getAdminFunction(detail.function.id).then(setDetail), reloadList()])}
              onDelete={async () => {
                if (!confirm(`Delete function "${detail.function.name}"? This removes ${detail.members.length} membership${detail.members.length === 1 ? '' : 's'}.`)) return;
                await deleteAdminFunction(detail.function.id);
                setActiveId(null);
                setDetail(null);
                reloadList();
              }}
            />
          : <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Select a function on the left, or add one.</div>}
      </div>

      {createOpen && (
        <CreateFunctionModal
          onClose={() => setCreateOpen(false)}
          onCreated={async fn => {
            setCreateOpen(false);
            await reloadList();
            setActiveId(fn.id);
          }}
        />
      )}
      </div>
    </div>
  );
};

const FunctionEditor = ({ detail, users, currentUser, onReload, onDelete }) => {
  const fn = detail.function;
  const [name, setName] = React.useState(fn.name);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [evaluating, setEvaluating] = React.useState(false);
  const [evalReasoning, setEvalReasoning] = React.useState(null);
  const [mdPickerOpen, setMdPickerOpen] = React.useState(false);

  // Staffing capacity (migration 0071). Three fields surfaced inline
  // so admins can flip a role into the firm-wide capacity chart and
  // set the working-week shape + target chargeability in one place.
  const [clientFacing, setClientFacing] = React.useState(!!fn.isClientFacing);
  const [weeklyHours,  setWeeklyHours]  = React.useState(fn.weeklyHoursDefault ?? '');
  const [targetPct,    setTargetPct]    = React.useState(fn.chargeabilityTargetPct ?? 80);

  // Sync local form fields with the upstream record whenever it
  // changes from outside — agent edit, parallel admin save, etc.
  // `useState(fn.name)` only fires on mount; without this effect
  // the name input shows the value at the time the editor was
  // first opened forever. `key={fn.id}` on the parent (admin.jsx
  // line ~815) covers the "different row selected" case; this
  // effect covers the "same row, fresh data" case.
  // Resync only when a different function is selected. We don't key on
  // updatedAt: auto-save reloads the row after every edit, and re-pulling
  // here would clobber edits made during the save round-trip.
  React.useEffect(() => {
    setName(fn.name);
    setClientFacing(!!fn.isClientFacing);
    setWeeklyHours(fn.weeklyHoursDefault ?? '');
    setTargetPct(fn.chargeabilityTargetPct ?? 80);
  }, [fn.id]);

  const completenessPct = fn.completenessPct ?? 0;
  const agenticPct      = fn.agenticPct ?? 0;
  const mdPath          = fn.mdPath || null;
  const capacityDirty =
    clientFacing !== !!fn.isClientFacing ||
    (weeklyHours === '' ? fn.weeklyHoursDefault != null
                        : Number(weeklyHours) !== (fn.weeklyHoursDefault ?? null)) ||
    Number(targetPct) !== (fn.chargeabilityTargetPct ?? 80);
  const dirty = name !== fn.name || capacityDirty;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await patchAdminFunction(fn.id, {
        name: name.trim(),
        isClientFacing: clientFacing,
        weeklyHoursDefault: weeklyHours === '' ? null : Number(weeklyHours),
        chargeabilityTargetPct: Number(targetPct),
      });
      await onReload();
      setName(name.trim()); // settle `dirty` even if the input had trailing space
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const { status: saveStatus } = useAutoSave({
    dirty,
    save,
    signature: `${name}|${clientFacing}|${weeklyHours}|${targetPct}`,
  });

  const [liveCoverage, setLiveCoverage] = React.useState(null);
  const [coverageStatus, setCoverageStatus] = React.useState(/** @type {null | 'absent' | 'empty'} */ (null));
  const evaluate = async () => {
    setEvaluating(true); setError(null); setEvalReasoning(null); setLiveCoverage(null); setCoverageStatus(null);
    try {
      const r = await evaluateAdminFunction(fn.id);
      setEvalReasoning(r.reasoning || '');
      if (Array.isArray(r.coverage)) {
        setLiveCoverage(r.coverage);
        if (r.coverage.length === 0) setCoverageStatus('empty');
      } else {
        // Field missing from the response → the deployed Functions
        // build is still on the old evaluator code.
        setCoverageStatus('absent');
      }
      await onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setEvaluating(false);
    }
  };
  // Prefer the just-evaluated coverage if present, otherwise fall back
  // to what's on the function record from the API.
  const coverage = liveCoverage || (Array.isArray(fn.coverage) ? fn.coverage : []);

  const viewMd = () => {
    const path = mdPath || `/roles/${fn.slug}.md`;
    try { sessionStorage.setItem('erasphere.orgrepoPath', path); } catch {}
    location.hash = 'orgrepo';
  };

  const setMdPath = async newPath => {
    setBusy(true); setError(null);
    try {
      await patchAdminFunction(fn.id, { mdPath: newPath });
      await onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!confirm(`Disconnect the MD file from "${fn.name}"? The file stays in OrgRepo — only the binding is removed.`)) return;
    await setMdPath(null);
  };

  const agenticLabel =
    agenticPct === 0   ? 'Full human' :
    agenticPct === 100 ? 'Full agentic' :
    `Hybrid ${100 - agenticPct}/${agenticPct}`;

  return (
    <div style={{background:'var(--bg-alt)',borderRadius:12,padding:18}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        <Icon name="file" size={13}/>
        <span style={{fontFamily:'JetBrains Mono',fontSize:12,color:'var(--fg-muted)'}}>/functions/{fn.slug}</span>
        <div style={{flex:1}}/>
        <span style={{fontSize:11,color:'var(--fg-muted)'}}>{detail.members.length} member{detail.members.length === 1 ? '' : 's'}</span>
        <button className="btn-ghost" style={{padding:'4px 10px',fontSize:12}} onClick={onDelete}>Delete</button>
        <SaveStatus status={saveStatus} error={error} dirty={dirty}/>
      </div>

      {error && <div style={{padding:10,marginBottom:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

      <div className="field"><label>Name</label><input className="input" value={name} onChange={e => setName(e.target.value)}/></div>

      <div className="field" style={{padding:'10px 12px',background:'var(--bg-card)',borderRadius:8}}>
        <label style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <span>Staffing capacity</span>
          <span style={{fontSize:11,color:'var(--fg-muted)'}}>
            {clientFacing ? 'Counts towards firm capacity' : 'Not in firm capacity'}
          </span>
        </label>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr 1fr',gap:10,alignItems:'center'}}>
          <label style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
            <input type="checkbox" checked={clientFacing} onChange={e => setClientFacing(e.target.checked)}/>
            Client-facing
          </label>
          <div>
            <div style={{fontSize:10.5,color:'var(--fg-muted)',marginBottom:2}}>Weekly hours (default)</div>
            <input
              className="input"
              type="number" min="0" max="168" step="1"
              placeholder="40"
              value={weeklyHours}
              onChange={e => setWeeklyHours(e.target.value)}
              style={{width:'100%'}}
            />
          </div>
          <div>
            <div style={{fontSize:10.5,color:'var(--fg-muted)',marginBottom:2}}>Chargeability target (%)</div>
            <input
              className="input"
              type="number" min="0" max="100" step="5"
              value={targetPct}
              onChange={e => setTargetPct(e.target.value)}
              style={{width:'100%'}}
            />
          </div>
        </div>
        <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:8,lineHeight:1.5}}>
          Members below the target on a given week get an orange <span style={{color:'var(--orange)',fontWeight:700}}>!</span> in the Staffing grid.
          Per-person overrides live on the user profile.
        </div>
      </div>

      <div className="field" style={{padding:'10px 12px',background:'var(--bg-card)',borderRadius:8}}>
        <label style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>Role MD file</span>
          <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{mdPath || '— none —'}</span>
        </label>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
          <button type="button" className="btn-ghost" style={{padding:'4px 10px',fontSize:12,gap:4}} onClick={viewMd} disabled={!mdPath} title={mdPath ? `Open ${mdPath} in OrgRepo` : 'Connect an MD file first'}><Icon name="file-text" size={12}/> View</button>
          <button type="button" className="btn-ghost" style={{padding:'4px 10px',fontSize:12}} onClick={() => setMdPickerOpen(true)} disabled={busy}>{mdPath ? '↻ Change' : '+ Connect'}</button>
          {mdPath && (
            <button type="button" className="btn-ghost" style={{padding:'4px 10px',fontSize:12,color:'var(--support-red)'}} onClick={disconnect} disabled={busy}>Disconnect</button>
          )}
        </div>
        <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:6}}>
          Bundles a markdown file from OrgRepo to this function. The Evaluate button reads from this file; people in the function see it as their role doc.
        </div>
      </div>

      {mdPickerOpen && (
        <MdPickerModal
          currentPath={mdPath}
          slug={fn.slug}
          onClose={() => setMdPickerOpen(false)}
          onPick={async path => { await setMdPath(path); setMdPickerOpen(false); }}
        />
      )}

      <div className="respo-2col" style={{marginTop:6}}>
        <div className="field" style={{margin:0}}>
          <label style={{display:'flex',justifyContent:'space-between',alignItems:'baseline'}}>
            <span>MD completeness</span>
            <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{completenessPct}%</span>
          </label>
          <div style={{height:8,background:'var(--bg-card)',borderRadius:4,overflow:'hidden'}}>
            <div style={{width:`${completenessPct}%`,height:'100%',background:'var(--orange)'}}/>
          </div>
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
            Read-only — set by Claude evaluating the role's MD file.
          </div>
        </div>

        <div className="field" style={{margin:0}}>
          <label style={{display:'flex',justifyContent:'space-between',alignItems:'baseline'}}>
            <span>Human ↔ Agentic</span>
            <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{agenticLabel}</span>
          </label>
          <div style={{height:8,background:'var(--bg-card)',borderRadius:4,overflow:'hidden'}}>
            <div style={{width:`${agenticPct}%`,height:'100%',background:'var(--support-purple)'}}/>
          </div>
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
            Read-only — Claude's call on how much of this role could run agentically based on the MD.
          </div>
        </div>
      </div>

      <div style={{marginTop:14,display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <button
          className="btn-ghost"
          style={{padding:'5px 12px',fontSize:12}}
          onClick={evaluate}
          disabled={evaluating}
          title="Ask Claude to re-read the MD and update both scores"
        >
          {evaluating ? 'Evaluating…' : '✨ Evaluate via MD'}
        </button>
        {evalReasoning && (
          <div style={{flex:1,minWidth:240,fontSize:12,color:'var(--fg-muted)',padding:'6px 10px',background:'var(--bg-card)',borderRadius:8}}>
            <strong style={{color:'var(--fg)'}}>Claude:</strong> {evalReasoning}
          </div>
        )}
      </div>

      <CoverageMap coverage={coverage} hasEvaluated={completenessPct > 0 || agenticPct > 0} status={coverageStatus} roleName={fn.name}/>

      <div className="section__head" style={{marginTop:16}}><h2 style={{fontSize:14}}>Members</h2></div>
      <MemberManager
        functionId={fn.id}
        members={detail.members}
        users={users}
        currentUser={currentUser}
        onChanged={onReload}
      />
    </div>
  );
};

/* Slug + title generators for the "Draft skill from gap" handoff.
 * Trims to a 4-ish word kebab-case slug; the admin can rewrite it
 * in the modal before saving. */
function slugFromResponsibility(resp) {
  const STOP = new Set(['the','a','an','of','for','to','in','on','and','or','with','without','at','by','from','their','its']);
  return (resp || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter(w => w && !STOP.has(w))
    .slice(0, 4)
    .join('-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'new-skill';
}
function titleFromSlug(slug) {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/* Drop a skill draft into sessionStorage and navigate to the Skills
 * view, where the Create modal opens pre-filled. Keys must match
 * what workers.jsx → consumeSkillDraft expects. */
function draftSkillFromGap({ roleName, responsibility, note }) {
  const slug = slugFromResponsibility(responsibility);
  const draft = {
    id: slug,
    name: titleFromSlug(slug),
    category: 'Synthesize',
    description: `${responsibility}${note ? ` — ${note}` : ''}`,
    inputs: [
      { key: 'context', label: 'Inputs / context for this responsibility', required: true, multiline: true, placeholder: 'Paste the data, request, or prior step output the skill should work from.' },
    ],
    systemPrompt: '',
    userTemplate: `You're acting on behalf of the ${roleName} role.\n\nResponsibility: ${responsibility}${note ? `\nWhy this is currently a gap: ${note}` : ''}\n\nContext to work from:\n{{context}}\n\nProduce a clear, structured output that fulfils the responsibility. Cite assumptions explicitly.`,
    maxTokens: 1600,
    _draftedFromGap: `${roleName} · ${responsibility}`,
  };
  try {
    sessionStorage.setItem('erasphere.skillDraft', JSON.stringify(draft));
  } catch { /* private mode etc. */ }
  location.hash = 'skills';
}

/* Coverage map: every distinct responsibility the role MD lists,
 * paired with the skill / agent / process the system already has
 * to fulfil it. Comes from /api/admin/functions/:id/evaluate. */
const CoverageMap = ({ coverage, hasEvaluated, status, roleName }) => {
  if (!coverage.length) {
    let msg;
    if (status === 'absent') {
      msg = (
        <>
          The evaluator responded, but the response had no <code>coverage</code> field — the deployed back-end is still on the old evaluator code. The new Functions deploy hasn't propagated yet. Wait a minute and click Evaluate again.
        </>
      );
    } else if (status === 'empty') {
      msg = 'The evaluator returned an empty coverage array. Re-run Evaluate; if it stays empty, the role MD is too thin for Claude to extract distinct responsibilities — flesh out the "What they do" section first.';
    } else if (hasEvaluated) {
      msg = (
        <>
          This role has scores but no coverage map — it was evaluated under the old code. Click <strong>✨ Evaluate via MD</strong> to compute it now. (If the table stays empty, check that <em>Deploy infra</em> in Actions ran since migration 0029.)
        </>
      );
    } else {
      msg = 'Click ✨ Evaluate via MD to compute, per responsibility, which skill / agent / process already covers it.';
    }
    return (
      <div style={{marginTop:18,padding:'12px 14px',background:'var(--bg-card)',borderRadius:8,fontSize:12,color:'var(--fg-muted)'}}>
        <strong style={{color:'var(--fg)',fontSize:13}}>Coverage map</strong>
        <div style={{marginTop:4}}>{msg}</div>
      </div>
    );
  }
  const VERDICT_COLOR = {
    covered: { fg: '#00702A', bg: '#DCF1E2', label: 'Covered' },
    partial: { fg: '#B83C0F', bg: '#FFE7DC', label: 'Partial' },
    gap:     { fg: '#A20235', bg: '#FCE6E0', label: 'Gap' },
  };
  return (
    <div style={{marginTop:18}}>
      <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',marginBottom:6}}>
        <div style={{fontSize:13,fontWeight:600}}>Coverage map · {coverage.length} responsibilit{coverage.length === 1 ? 'y' : 'ies'}</div>
        <div style={{fontSize:11,color:'var(--fg-muted)'}}>
          {coverage.filter(c => c.verdict === 'covered').length} covered ·
          {' '}{coverage.filter(c => c.verdict === 'partial').length} partial ·
          {' '}{coverage.filter(c => c.verdict === 'gap').length} gap
        </div>
      </div>
      <div style={{background:'var(--bg-card)',borderRadius:8,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
          <thead>
            <tr style={{background:'var(--bg-alt)',color:'var(--fg-muted)',fontSize:11}}>
              <th style={{textAlign:'left',padding:'8px 10px',fontWeight:500}}>Responsibility</th>
              <th style={{textAlign:'left',padding:'8px 10px',fontWeight:500,width:90}}>Verdict</th>
              <th style={{textAlign:'left',padding:'8px 10px',fontWeight:500}}>Covered by</th>
              <th style={{textAlign:'right',padding:'8px 10px',fontWeight:500,width:60}}>Score</th>
              <th style={{textAlign:'right',padding:'8px 10px',fontWeight:500,width:110}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {coverage.map((c, i) => {
              const c0 = VERDICT_COLOR[c.verdict] || VERDICT_COLOR.gap;
              const isActionable = c.verdict === 'gap' || c.verdict === 'partial';
              return (
                <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 10px',verticalAlign:'top'}}>
                    <div>{c.responsibility}</div>
                    {c.note && <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:2}}>{c.note}</div>}
                  </td>
                  <td style={{padding:'8px 10px',verticalAlign:'top'}}>
                    <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,background:c0.bg,color:c0.fg}}>{c0.label}</span>
                  </td>
                  <td style={{padding:'8px 10px',verticalAlign:'top',color:c.via.length ? 'var(--fg)' : 'var(--fg-muted)'}}>
                    {c.via.length
                      ? c.via.map((v, j) => (
                          <span key={j} style={{display:'inline-block',padding:'1px 6px',marginRight:4,marginBottom:2,background:'var(--bg-alt)',borderRadius:4,fontFamily:'JetBrains Mono',fontSize:11}}>{v}</span>
                        ))
                      : <em>— nothing in the system covers this yet —</em>}
                  </td>
                  <td style={{padding:'8px 10px',textAlign:'right',verticalAlign:'top',fontFamily:'JetBrains Mono',color:'var(--fg-muted)'}}>{c.score}%</td>
                  <td style={{padding:'8px 10px',textAlign:'right',verticalAlign:'top'}}>
                    {isActionable && (
                      <button
                        type="button"
                        className="btn-ghost"
                        style={{padding:'3px 9px',fontSize:11.5,whiteSpace:'nowrap'}}
                        onClick={() => draftSkillFromGap({ roleName, responsibility: c.responsibility, note: c.note })}
                        title={c.verdict === 'gap'
                          ? 'No skill covers this. Draft one to close the gap — opens the Create Skill modal pre-filled.'
                          : 'Partial coverage. Draft a dedicated skill — opens the Create Skill modal pre-filled.'}
                      >
                        + Draft skill
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MemberManager = ({ functionId, members, users, currentUser, onChanged }) => {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const memberIds = new Set(members.map(m => m.userId));
  const available = users.filter(u => !memberIds.has(u._id || u.id));

  const add = async (userId) => {
    setBusy(true);
    try { await addFunctionMember(functionId, { userId }); await onChanged(); setPickerOpen(false); }
    finally { setBusy(false); }
  };

  const remove = async userId => {
    setBusy(true);
    try { await removeFunctionMember(functionId, userId); await onChanged(); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {members.length === 0 && (
        <div style={{padding:12,color:'var(--fg-muted)',fontSize:13,background:'var(--bg-card)',borderRadius:8,marginBottom:8}}>
          No members yet.
        </div>
      )}

      {members.map(m => (
        <div key={m.userId} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid var(--bg-card)',cursor:'pointer'}} onClick={() => { window.location.hash = 'people/' + m.userId; }}>
          <Avatar name={m.name} size={28}/>
          <div>
            <div style={{fontSize:13,fontWeight:500}}>{m.name}</div>
            <div style={{fontSize:11,color:'var(--fg-muted)'}}>{m.email}</div>
          </div>
          <div style={{flex:1}}/>
          <button
            className="btn-ghost"
            style={{padding:'3px 8px',fontSize:11}}
            disabled={busy}
            onClick={e => { e.stopPropagation(); remove(m.userId); }}
          >
            Remove
          </button>
        </div>
      ))}

      <div style={{marginTop:12}}>
        <button className="btn-ghost" onClick={() => setPickerOpen(o => !o)}>
          {pickerOpen ? 'Close picker' : '+ Add member'}
        </button>
        {pickerOpen && (
          <div style={{marginTop:10,padding:12,background:'var(--bg-card)',borderRadius:8}}>
            {available.length === 0 && (
              <div style={{fontSize:12,color:'var(--fg-muted)'}}>No more users to add.</div>
            )}
            {available.map(u => (
              <div key={u._id || u.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0'}}>
                <Avatar name={u.name} size={24}/>
                <span style={{fontSize:13}}>{u.name}</span>
                <span style={{fontSize:11,color:'var(--fg-muted)'}}>{u.email}</span>
                <div style={{flex:1}}/>
                <button className="btn-orange" style={{padding:'2px 8px',fontSize:11}} disabled={busy}
                        onClick={() => add(u._id || u.id)}>+ Add</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CreateFunctionModal = ({ onClose, onCreated }) => {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const fn = await createAdminFunction({ name: name.trim() });
      onCreated(fn);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:520}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>New function</div>
        <h3>Define a new function.</h3>
        <p style={{color:'var(--fg-muted)',fontSize:14,marginBottom:20,marginTop:6}}>
          Functions are the firm's role catalog. We auto-create a markdown file under{' '}
          <code style={{fontFamily:'JetBrains Mono',fontSize:11.5}}>/roles/&lt;slug&gt;.md</code>{' '}
          — fill that in to define the role.
        </p>

        <form onSubmit={submit}>
          <div className="field"><label>Name</label>
            <input className="input" placeholder="e.g. Senior Consultant" value={name} onChange={e => setName(e.target.value)} autoFocus/>
          </div>

          {error && <div style={{marginBottom:14,padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create function'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Errors & Optimizations queue ────────────────────────────────
 * Two sub-views: human/agent feedback rows (the things the AI did
 * poorly that need fixing) and proposed intents (multi-skill chat
 * observations awaiting promotion). One mental model for admins:
 * "what's the AI doing badly + what new shapes is it doing
 * repeatedly?"
 */


const AdminFeedback = () => {
  const [section, setSection] = React.useState('feedback');   // 'feedback' | 'intents'
  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:18}}>
        {[
          { id: 'feedback', label: 'Errors & optimizations' },
          { id: 'intents',  label: 'Proposed intents' },
        ].map(o => (
          <span
            key={o.id}
            onClick={() => setSection(o.id)}
            className={`tag ${section === o.id ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
          >
            {o.label}
          </span>
        ))}
      </div>
      {section === 'feedback' && <FeedbackQueue/>}
      {section === 'intents' && <ProposedIntents/>}
    </div>
  );
};

const FEEDBACK_TABS = [
  // "Needs review" is the default — combines open (fresh human
  // submissions waiting for the optimizer) and proposed (AI has
  // drafted a fix). Without this the queue defaulted to 'proposed'
  // and freshly-submitted human thumbs were invisible until the next
  // 01:30 optimizer pass promoted them.
  { id: 'needs-review', label: 'Needs review' },
  { id: 'open',         label: 'Open (raw)' },
  { id: 'proposed',     label: 'With proposal' },
  { id: 'applied',      label: 'Applied' },
  { id: 'dismissed',    label: 'Dismissed' },
];

// Top-of-queue source selector. Keys on the row's `sourceKind` — the
// producer that authored it — so admins can isolate one feed at a time.
const FEEDBACK_SOURCES = [
  { id: 'all',           label: 'All' },
  { id: 'chat-eval',     label: 'Auto chat analysis' },
  { id: 'user-feedback', label: 'User chat feedback' },
  { id: 'agent-eval',    label: 'Agent-run analysis' },
  { id: 'missing-skill', label: 'Missing skills' },
];

const FeedbackQueue = () => {
  const [tab, setTab] = React.useState('needs-review');
  const [sourceFilter, setSourceFilter] = React.useState('all');
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    listAdminFeedback(tab).then(setItems).catch(err => setError(err.message));
  }, [tab]);
  React.useEffect(() => { reload(); }, [reload]);

  const filtered = (items || []).filter(r =>
    sourceFilter === 'all' ? true : (r.sourceKind || 'user-feedback') === sourceFilter,
  );

  const decide = async (id, decision, noteOverride) => {
    // Bulk-apply callers (group-level Commit-all / Discard-all) prompt
    // for one shared note and pass it via noteOverride; individual
    // per-voice buttons leave it undefined and we prompt here.
    let note;
    if (noteOverride !== undefined) {
      note = noteOverride;
    } else {
      const promptText = decision === 'applied'
        ? 'Brief note describing what change you committed (where + what):'
        : decision === 'reopen'
          ? 'Why are you re-opening this? (optional)'
          : 'Brief note describing why this was discarded (optional):';
      note = prompt(promptText, '') || '';
    }
    setBusy(id);
    try { await decideAdminFeedback(id, decision, note); await reload(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  const saveProposal = async (id, optimizationMd) => {
    setBusy(id);
    try { await saveAdminFeedbackOptimization(id, optimizationMd); await reload(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <FeedbackFlowGuide/>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        {FEEDBACK_TABS.map(t => (
          <span key={t.id} onClick={() => setTab(t.id)}
                className={`tag ${tab === t.id ? 'tag--orange' : ''}`}
                style={{cursor:'pointer'}}>
            {t.label}
          </span>
        ))}
        <span style={{marginLeft:14,fontSize:11,color:'var(--fg-muted)'}}>Source:</span>
        {FEEDBACK_SOURCES.map(o => (
          <span key={o.id} onClick={() => setSourceFilter(o.id)}
                className={`tag ${sourceFilter === o.id ? 'tag--orange' : ''}`}
                style={{cursor:'pointer',fontSize:11}}>
            {o.label}
          </span>
        ))}
      </div>
      {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
      {items === null && <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}
      {items && filtered.length === 0 && (
        <div style={{padding:20,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13,textAlign:'center'}}>
          No rows in this state{sourceFilter === 'all' ? '' : ` for ${(FEEDBACK_SOURCES.find(s => s.id === sourceFilter) || {}).label || sourceFilter}`}.
        </div>
      )}
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        {/* Group rows by their grouper (intent for chat sources, agent
            for agent runs, skill for role gaps) so every finding about
            the same intent / agent / skill renders as one card with the
            contributions stacked inside. Each voice keeps its own DB row
            + lifecycle; group-level buttons let admins apply one decision
            to all undecided voices at once. */}
        {groupFeedbackByGrouper(filtered).map(g => (
          <GroupedFeedbackCard
            key={g.key}
            group={g}
            onDecide={decide}
            onSaveProposal={saveProposal}
            busyId={busy}
          />
        ))}
      </div>
    </div>
  );
};

/** Top-of-queue explainer that lays out the lifecycle once so admins
 *  don't have to figure it out from the buttons. */
const FeedbackFlowGuide = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{
      padding: open ? '14px 16px' : '8px 14px',
      marginBottom:14,
      background:'var(--bg-alt)', borderRadius:10,
      fontSize:12.5, lineHeight:1.55,
    }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:'var(--fg-muted)'}}
      >
        <Icon name="chevron-right" size={11} stroke="currentColor"/>
        <span style={{transform: open ? 'translateY(0)' : 'none'}}>
          {open ? 'How this queue works' : 'How this queue works — click to expand'}
        </span>
      </div>
      {open && (
        <div style={{marginTop:10,color:'var(--fg)',display:'flex',flexDirection:'column',gap:8}}>
          <div>
            <strong>Lifecycle:</strong> <code>open</code> → <code>proposed</code> → <code>applied</code> or <code>dismissed</code>. Any decided row can be <code>reopened</code> back to <code>proposed</code> — discards are kept, not deleted.
          </div>
          <div>
            <strong>open</strong>: row just landed (human thumbs-down or agent flagging a problem). The 01:30 Zurich <em>feedback-optimizer</em> master picks it up and drafts the proposal.
          </div>
          <div>
            <strong>proposed</strong>: the AI has drafted a <em>Suggested improvement</em>. Each card explains whether the fix is <strong>applied directly in the app</strong> (OrgRepo edit, master agent prompt edit, user-skill edit) or whether it <strong>needs Claude Code</strong> (built-in skill / runtime change). You can also <em>edit the proposal</em> before committing if the AI didn't get it quite right.
          </div>
          <div>
            <strong>applied</strong>: you (the admin) committed the change. Record what you did in the note so the audit trail is clear.
          </div>
          <div>
            <strong>dismissed</strong>: not actionable. Sits in the Dismissed tab so you can re-open it later if you change your mind.
          </div>
        </div>
      )}
    </div>
  );
};

/** Five categories the feedback-optimizer asks Claude to tag the
 *  proposal with. Parsed client-side from optimization_md. */
const KIND_META = {
  'PROMPT-EDIT':     { label: 'Prompt edit',    color: '#3B2D8C' },
  'INTENT-EDIT':     { label: 'Intent edit',    color: '#1A6A2B' },
  'SKILL-EDIT':      { label: 'Skill edit',     color: '#9F4A0F' },
  'DATA-SOURCE-FIX': { label: 'Data source fix',color: '#10484C' },
  'NO-CHANGE':       { label: 'No change',      color: '#6B6457' },
};

function detectKind(md) {
  if (!md) return null;
  const m = md.match(/(PROMPT-EDIT|INTENT-EDIT|SKILL-EDIT|DATA-SOURCE-FIX|NO-CHANGE)/);
  return m ? m[1] : null;
}

function detectIntentPath(md) {
  if (!md) return null;
  const m = md.match(/\/(?:orgrepo\/)?intents\/([a-z0-9-]+)\.md/i);
  return m ? `/intents/${m[1]}.md` : null;
}

function detectMasterAgentSlug(md) {
  if (!md) return null;
  const m = md.match(/\b(news-clients|news-services|news-industries|eve-personal-development|morning-briefings|feedback-optimizer|user-insights|missing-skills-detector|self-learning-chat-eval|agent-run-evaluator|strategy-cascade)\b/);
  return m ? m[1] : null;
}

/** Group rows by (target_type, target_id) so multiple voices on the
 *  same chat reply / agent run / briefing land on one card. Within a
 *  group, voices are ordered newest first; groups themselves are
 *  ordered by their most-recent voice. */
// Bucket label shown on the group chip, per grouper kind.
const GROUPER_KIND_LABEL = { intent: 'intent', agent: 'agent', skill: 'skill', other: '' };

function groupFeedbackByGrouper(rows) {
  const map = new Map();
  for (const r of rows) {
    const gKind = r.grouperKind || 'other';
    const gKey = r.grouperKey || `${r.targetType}:${r.targetId}`;
    const key = `${gKind}::${gKey}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        grouperKind: gKind,
        grouperKey: gKey,
        grouperLabel: r.grouperLabel || r.targetLabel,
        outputLink: r.outputLink,
        voices: [],
      });
    }
    const g = map.get(key);
    g.voices.push(r);
    // Prefer the most descriptive label / output link across voices.
    if (!g.grouperLabel && (r.grouperLabel || r.targetLabel)) g.grouperLabel = r.grouperLabel || r.targetLabel;
    if (!g.outputLink && r.outputLink) g.outputLink = r.outputLink;
  }
  const list = [...map.values()];
  for (const g of list) {
    g.voices.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    g.mostRecentAt = new Date(g.voices[0].createdAt).getTime();
  }
  list.sort((a, b) => b.mostRecentAt - a.mostRecentAt);
  return list;
}

const GroupedFeedbackCard = ({ group, onDecide, onSaveProposal, busyId }) => {
  const undecided = group.voices.filter(v => v.status !== 'applied' && v.status !== 'dismissed');
  const allBusy = busyId && group.voices.some(v => v.id === busyId);

  const bulkDecide = async decision => {
    if (undecided.length === 0) return;
    const promptText = decision === 'applied'
      ? `Committing ${undecided.length} voice${undecided.length === 1 ? '' : 's'} on this output. Note describing what you did:`
      : `Discarding ${undecided.length} voice${undecided.length === 1 ? '' : 's'} on this output. Reason (optional):`;
    const note = prompt(promptText, '') || '';
    for (const v of undecided) {
      try { await onDecide(v.id, decision, note); }
      catch { /* per-row error surfaces in the parent */ }
    }
  };

  return (
    <div style={{padding:16,background:'var(--bg-alt)',borderRadius:12,display:'flex',flexDirection:'column',gap:12}}>
      {/* Shared header — once per group */}
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <span className="tag" style={{fontFamily:'JetBrains Mono',fontSize:10,background:'var(--bg)',color:'var(--fg-muted)'}}>
          {(GROUPER_KIND_LABEL[group.grouperKind] || group.grouperKind || 'item')}
          {group.grouperKind && group.grouperKind !== 'other' ? `: ${group.grouperKey}` : ''}
        </span>
        <span style={{fontSize:13,fontWeight:600,minWidth:0,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {group.grouperLabel || group.grouperKey.slice(0, 32)}
        </span>
        <span style={{fontSize:11,color:'var(--fg-muted)'}}>
          {group.voices.length} voice{group.voices.length === 1 ? '' : 's'}
        </span>
        {group.outputLink && (
          <a href={group.outputLink} className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
            Open output
          </a>
        )}
      </div>

      {/* Voice rows */}
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {group.voices.map((v, idx) => (
          <React.Fragment key={v.id}>
            {idx > 0 && <div style={{borderTop:'1px dashed var(--border)',margin:'2px 0'}}/>}
            <VoiceRow
              voice={v}
              onDecide={onDecide}
              onSaveProposal={onSaveProposal}
              busy={busyId === v.id}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Group-level bulk action — only when there are ≥2 undecided voices */}
      {undecided.length >= 2 && (
        <div style={{
          display:'flex',gap:8,justifyContent:'flex-end',
          paddingTop:8,borderTop:'1px solid var(--border)',
          fontSize:11,
        }}>
          <span style={{fontSize:11,color:'var(--fg-muted)',alignSelf:'center',marginRight:'auto',fontStyle:'italic'}}>
            Apply one decision to all {undecided.length} undecided voices on this output:
          </span>
          <button className="btn-ghost" style={{padding:'4px 12px',fontSize:11}} disabled={allBusy} onClick={() => bulkDecide('dismissed')}>
            Discard all
          </button>
          <button className="btn-orange" style={{padding:'4px 12px',fontSize:11}} disabled={allBusy} onClick={() => bulkDecide('applied')}>
            Commit all
          </button>
        </div>
      )}
    </div>
  );
};

const VoiceRow = ({ voice: row, onDecide, onSaveProposal, busy }) => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(row.optimizationMd || '');
  React.useEffect(() => { setDraft(row.optimizationMd || ''); }, [row.optimizationMd]);

  const decided = row.status === 'applied' || row.status === 'dismissed';
  const ratingLabel = row.rating === 1 ? 'thumbs UP' : 'thumbs DOWN';
  const isAgent = row.source === 'agent';
  const byLabel = isAgent
    ? `by agent: ${row.submitterLabel || 'AI evaluator'}`
    : row.userName ? `by ${row.userName}` : null;
  const statusColor =
    row.status === 'applied'    ? '#136127' :
    row.status === 'dismissed'  ? '#6B6457' :
    row.status === 'proposed'   ? '#B83C0F' : '#3B2D8C';
  const kind = detectKind(row.optimizationMd);
  const kindMeta = kind ? KIND_META[kind] : null;

  const submitEdit = async () => {
    await onSaveProposal(row.id, draft);
    setEditOpen(false);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',fontSize:11.5}}>
        <span className="tag" style={{background:row.rating === 1 ? '#D7F3DB' : '#FCE6E0',color:row.rating === 1 ? '#136127' : '#A20235',fontSize:10.5}}>
          {ratingLabel}
        </span>
        <span className="tag" style={{
          background: isAgent ? '#E8E0FF' : '#FFE7DC',
          color: isAgent ? '#3B2D8C' : '#B83C0F',
          fontSize: 10,
        }} title={isAgent ? 'Submitted automatically by an AI agent' : 'Submitted by a human user'}>
          {isAgent ? 'AGENT' : 'HUMAN'}
        </span>
        <span className="tag" style={{
          background:'var(--bg)', color: statusColor,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
        }}>
          {row.status.toUpperCase()}
        </span>
        {kindMeta && (
          <span className="tag" style={{background:'var(--bg)',color:kindMeta.color,fontSize:10,fontWeight:600}}>
            {kindMeta.label}
          </span>
        )}
        {byLabel && <span style={{color:'var(--fg-muted)'}}>{byLabel}</span>}
        <span style={{color:'var(--fg-muted)',marginLeft:'auto'}}>
          {new Date(row.createdAt).toLocaleString()}
        </span>
      </div>

      <div className="respo-2col" style={{gap:12,fontSize:12.5}}>
        <FeedbackBlock title="What the user asked" body={row.intentSnapshot}/>
        <FeedbackBlock title="What the AI did" body={row.aiDidSnapshot}/>
        <FeedbackBlock title="User reason" body={row.reason}/>
        <FeedbackBlock title="User suggestion" body={row.suggestion}/>
      </div>

      {/* Proposal block */}
      {row.optimizationMd && !editOpen && (
        <div style={{padding:10,background:'var(--bg)',borderRadius:8,fontSize:12.5,lineHeight:1.45}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
            <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--brand-orange)',fontWeight:600}}>
              AI proposal
            </div>
            <button className="btn-ghost" style={{padding:'2px 8px',fontSize:10.5}} onClick={() => setEditOpen(true)} disabled={busy}>
              Edit
            </button>
          </div>
          <MarkdownView source={row.optimizationMd}/>
        </div>
      )}
      {editOpen && (
        <div style={{padding:10,background:'var(--bg)',borderRadius:8}}>
          <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--brand-orange)',fontWeight:600,marginBottom:6}}>
            Edit proposal
          </div>
          <textarea
            rows={Math.max(6, Math.min(18, draft.split('\n').length + 1))}
            className="textarea"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{width:'100%',fontFamily:'inherit',fontSize:12.5,padding:8}}
          />
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
            <button className="btn-ghost" style={{padding:'4px 12px',fontSize:11.5}} onClick={() => { setDraft(row.optimizationMd || ''); setEditOpen(false); }} disabled={busy}>
              Cancel
            </button>
            <button className="btn-orange" style={{padding:'4px 12px',fontSize:11.5}} onClick={submitEdit} disabled={busy || draft === row.optimizationMd}>
              Save proposal
            </button>
          </div>
        </div>
      )}

      {kind && kind !== 'NO-CHANGE' && <SolutionPath kind={kind} optimizationMd={row.optimizationMd} row={row}/>}

      {row.decisionNote && (
        <div style={{fontSize:12,color:'var(--fg-muted)',fontStyle:'italic',borderLeft:'2px solid var(--border)',paddingLeft:8}}>
          Decision note: {row.decisionNote}
        </div>
      )}

      <div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
        {!decided && (
          <>
            <button className="btn-ghost" style={{padding:'4px 12px',fontSize:11.5}} onClick={() => onDecide(row.id, 'dismissed')} disabled={busy}>
              Discard
            </button>
            <button className="btn-orange" style={{padding:'4px 12px',fontSize:11.5}} onClick={() => onDecide(row.id, 'applied')} disabled={busy}>
              Commit
            </button>
          </>
        )}
        {decided && (
          <button className="btn-secondary" style={{padding:'4px 12px',fontSize:11.5}} onClick={() => onDecide(row.id, 'reopen')} disabled={busy}>
            Reactivate
          </button>
        )}
      </div>
    </div>
  );
};

/** Side-by-side guidance: where can this be applied directly in the
 *  running app, and what would need a Claude Code change instead.
 *  Both paths are always shown so admins know all their options. */
const SolutionPath = ({ kind, optimizationMd, row }) => {
  const intentPath = detectIntentPath(optimizationMd);
  const masterSlug = detectMasterAgentSlug(optimizationMd);

  const direct = renderDirectGuidance(kind, intentPath, masterSlug);
  const code   = renderCodeGuidance(kind, row);

  return (
    <div className="respo-2col" style={{
      gap:10,
      padding:12,background:'var(--bg)',borderRadius:8,
      fontSize:12,lineHeight:1.5,
    }}>
      <div style={{paddingRight:8,borderRight:'1px solid var(--border)'}}>
        <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--support-green)',marginBottom:6,fontWeight:700}}>
          Apply directly
        </div>
        {direct}
      </div>
      <div>
        <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'#3B2D8C',marginBottom:6,fontWeight:700}}>
          Or adapt in Claude Code
        </div>
        {code}
      </div>
    </div>
  );
};

function renderDirectGuidance(kind, intentPath, masterSlug) {
  if (kind === 'INTENT-EDIT') {
    return (
      <div>
        <div style={{marginBottom:6}}>Edit the intent file directly in OrgRepo. No deploy needed.</div>
        {intentPath ? (
          <a href={`#orgrepo/${intentPath.replace(/^\//, '')}`} className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
            Open {intentPath} →
          </a>
        ) : (
          <span style={{color:'var(--fg-muted)',fontStyle:'italic'}}>
            Open OrgRepo and pick the relevant <code>/intents/&lt;slug&gt;.md</code>.
          </span>
        )}
      </div>
    );
  }
  if (kind === 'PROMPT-EDIT') {
    return (
      <div>
        <div style={{marginBottom:6}}>
          If this is a master-agent prompt: edit the agent's <code>prompt_template</code> in Agents — no deploy needed.
        </div>
        {masterSlug ? (
          <a href={`#agents`} className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
            Open Agents → {masterSlug} →
          </a>
        ) : (
          <a href={`#agents`} className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
            Open Agents →
          </a>
        )}
      </div>
    );
  }
  if (kind === 'SKILL-EDIT') {
    return (
      <div>
        <div style={{marginBottom:6}}>
          If this is a user-defined skill: edit it in Skills. Built-in skills need a code change (right side).
        </div>
        <a href={`#skills`} className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
          Open Skills →
        </a>
      </div>
    );
  }
  if (kind === 'DATA-SOURCE-FIX') {
    return (
      <div style={{color:'var(--fg-muted)'}}>
        Most data-source fixes need a Claude Code change (right side). If it's a content file in OrgRepo, edit there.
      </div>
    );
  }
  return <div style={{color:'var(--fg-muted)',fontStyle:'italic'}}>—</div>;
}

const FILES_BY_KIND = {
  'PROMPT-EDIT':     ['functions/_lib/masters/*.ts (master agent system prompts)', 'functions/_lib/morning-briefing.ts (briefing prompt)', 'functions/api/chats/[id]/eval.ts (chat-eval prompt)', 'functions/api/chat.ts (chat system prompt)'],
  'INTENT-EDIT':     ['orgrepo/intents/<slug>.md (under source control — usually edit in OrgRepo instead)', 'functions/_lib/orgrepo-seed.ts (rebuild via npm run build:seed after editing)'],
  'SKILL-EDIT':      ['functions/_lib/skills.ts (built-in skill definitions)', 'orgrepo/skills/<slug>.md (the skill\'s docs)'],
  'DATA-SOURCE-FIX': ['functions/_lib/masters/news-*.ts (web-search lens)', 'functions/_lib/news.ts (entity_news schema)', 'functions/_lib/<whatever-fetches-the-data>.ts'],
};

function renderCodeGuidance(kind, row) {
  const files = FILES_BY_KIND[kind] || [];
  return (
    <div>
      <div style={{marginBottom:6}}>
        Open Claude Code on this repo, change the files below, run the build, commit, and push.
      </div>
      {files.length > 0 && (
        <ul style={{margin:0,paddingLeft:18,fontFamily:'JetBrains Mono',fontSize:11.5,color:'var(--fg-muted)',marginBottom:8}}>
          {files.map(f => <li key={f}>{f}</li>)}
        </ul>
      )}
      <ClaudeCodePrompt row={row} kind={kind} files={files}/>
    </div>
  );
}

/** Build a ready-to-paste Claude Code prompt from a feedback row.
 *
 *  Shape (deliberately slim — describes the problem, not a pre-baked
 *  solution, so Claude can pick the right fix without being steered):
 *
 *    # Feedback {id} — {category}
 *    {slim five-section brief from optimization_md}
 *
 *    ## Raw context
 *    (snapshots + ids + status — everything the AI needs in one shot)
 *
 *    ## What to do
 *    {build/test/commit boilerplate}
 */
function buildClaudeCodePrompt({ row, kind, files }) {
  const lines = [];
  const shortId = row.id.slice(0, 8);
  lines.push(`# Feedback ${shortId} — ${kind}`);
  lines.push('');
  // The slim brief (Intent / What happened / Expected / Why / Constraints)
  // is written by the evaluator and stored in optimization_md. If it's
  // missing, fall back to a minimal stub so the AI can diagnose from
  // the raw context below.
  if (row.optimizationMd && row.optimizationMd.trim()) {
    lines.push(row.optimizationMd.trim());
  } else {
    lines.push('## Intent');
    lines.push('(see raw context below — no slim brief was generated for this row)');
    lines.push('');
    lines.push('## What happened');
    lines.push('(see ai_did_snapshot in raw context)');
    lines.push('');
    lines.push('## Expected');
    lines.push('(diagnose from rating + reason in raw context)');
    lines.push('');
    lines.push('## Why it likely failed');
    lines.push('(needs diagnosis)');
    lines.push('');
    lines.push('## Constraints / hints');
    lines.push(`Category: ${kind}.`);
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Raw context');
  lines.push('');
  lines.push(`- **Feedback id:** \`${row.id}\``);
  lines.push(`- **Target:** ${row.targetType}${row.targetLabel ? ` — ${row.targetLabel}` : ''} (\`${row.targetId}\`)`);
  lines.push(`- **Source:** ${row.source || 'human'}${row.submitterLabel ? ` (${row.submitterLabel})` : ''}`);
  lines.push(`- **Rating:** ${row.rating === 1 ? 'thumbs up' : row.rating === -1 ? 'thumbs down' : 'neutral (agent-flagged)'}`);
  lines.push(`- **Status:** ${row.status}`);
  if (row.outputLink) lines.push(`- **Output link:** \`${row.outputLink}\``);
  lines.push('');
  lines.push('### intent_snapshot — user\'s full message');
  lines.push('');
  lines.push(row.intentSnapshot ? '```\n' + row.intentSnapshot.trim() + '\n```' : '_(not captured)_');
  lines.push('');
  lines.push('### ai_did_snapshot — AI\'s full response');
  lines.push('');
  lines.push(row.aiDidSnapshot ? '```\n' + row.aiDidSnapshot.trim() + '\n```' : '_(not captured)_');
  if (row.reason) {
    lines.push('');
    lines.push('### User\'s reason');
    lines.push(row.reason.trim());
  }
  if (row.suggestion) {
    lines.push('');
    lines.push('### User\'s suggestion');
    lines.push(row.suggestion.trim());
  }
  if (files.length > 0) {
    lines.push('');
    lines.push('### Likely files to touch');
    for (const f of files) lines.push(`- \`${f}\``);
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## What to do');
  lines.push('1. Read the slim brief above + the raw context. Decide the minimal change.');
  lines.push('2. Edit only what\'s needed. Match the repo\'s existing conventions.');
  lines.push('3. Run `npx tsc --noEmit` and `npm run build` — both must pass.');
  lines.push(`4. Commit with a message that references feedback \`${shortId}\` and push.`);
  lines.push(`5. Return to Admin → Errors & Optimizations, find feedback \`${shortId}\`, click **Commit** with the commit sha.`);
  return lines.join('\n');
}

const ClaudeCodePrompt = ({ row, kind, files }) => {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  // Build the default text whenever the row changes. Stored in state
  // so the admin can edit before copying.
  React.useEffect(() => {
    setText(buildClaudeCodePrompt({ row, kind, files }));
    setCopied(false);
  }, [row.id, row.optimizationMd, kind, files]);   // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Older browsers: fallback via a temporary textarea.
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta);
        ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* surface inline if clipboard API blocked */ }
  };

  const reset = () => {
    setText(buildClaudeCodePrompt({ row, kind, files }));
    setCopied(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-secondary"
        style={{padding:'4px 10px',fontSize:11,marginTop:4}}
      >
        Generate Claude Code prompt →
      </button>
    );
  }

  return (
    <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
        <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'#3B2D8C',fontWeight:600}}>
          Paste into Claude Code on this repo
        </div>
        <div style={{display:'flex',gap:6}}>
          <button type="button" className="btn-ghost" style={{padding:'3px 8px',fontSize:10.5}} onClick={() => setOpen(false)}>
            Hide
          </button>
          <button type="button" className="btn-ghost" style={{padding:'3px 8px',fontSize:10.5}} onClick={reset}>
            Reset
          </button>
          <button type="button" className="btn-orange" style={{padding:'3px 12px',fontSize:10.5}} onClick={copy}>
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        </div>
      </div>
      <textarea
        rows={Math.min(20, Math.max(8, text.split('\n').length))}
        className="textarea"
        value={text}
        onChange={e => setText(e.target.value)}
        spellCheck={false}
        style={{
          width:'100%',
          fontFamily:'JetBrains Mono, ui-monospace, monospace',
          fontSize:11,
          lineHeight:1.5,
          padding:8,
          background:'var(--bg)',
        }}
      />
      <div style={{fontSize:10.5,color:'var(--fg-muted)',lineHeight:1.4}}>
        Edit if needed, then <strong>Copy</strong> and paste into Claude Code (run on this repo). Claude will edit the right files, build, commit, and push. When you've verified the commit, click <strong>Commit</strong> below with the sha as your note.
      </div>
    </div>
  );
};

const FeedbackBlock = ({ title, body }) => (
  <div>
    <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>
      {title}
    </div>
    <div style={{whiteSpace:'pre-wrap',lineHeight:1.45,color:body ? 'inherit' : 'var(--fg-muted)',fontStyle:body ? 'normal' : 'italic'}}>
      {body || '(none)'}
    </div>
  </div>
);

/* ─── Proposed intents (multi-skill chat observations) ──────────── */

const INTENT_TABS = [
  { id: 'observed',  label: 'Awaiting review' },
  { id: 'promoted',  label: 'Promoted' },
  { id: 'dismissed', label: 'Dismissed' },
];

const ProposedIntents = () => {
  const [tab, setTab] = React.useState('observed');
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    listProposedIntents(tab).then(setItems).catch(err => setError(err.message));
  }, [tab]);
  React.useEffect(() => { reload(); }, [reload]);

  const promote = async (obs, kind) => {
    const folder = kind === 'agent' ? '/agents' : '/intents';
    const slug = prompt(
      `Slug for the new ${kind} file (lowercase, hyphens). Will be written to ${folder}/<slug>.md. Leave blank for auto:`,
      '',
    ) || '';
    let parentIntentSlug;
    if (kind === 'agent') {
      parentIntentSlug = prompt(
        'Parent intent slug (the goal this agent fulfils, e.g. "draft-sow"). Leave blank to fill in later:',
        obs.matchedIntentSlug && obs.matchedIntentSlug !== 'freelance' ? obs.matchedIntentSlug : '',
      )?.trim() || undefined;
    }
    setBusy(obs.patternKey);
    try {
      await promoteIntentObservation(obs.patternKey, slug.trim() || null, { kind, parentIntentSlug });
      await reload();
    }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };
  const dismiss = async (obs) => {
    if (!confirm('Dismiss this observation? It can be re-observed later if the pattern repeats.')) return;
    setBusy(obs.patternKey);
    try { await dismissIntentObservation(obs.patternKey); await reload(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{padding:14,marginBottom:14,background:'var(--bg-alt)',borderRadius:10,fontSize:12.5,lineHeight:1.5,color:'var(--fg-muted)'}}>
        Every chat that invokes <strong>2+ recognised skills</strong> increments a row here. <strong>Nothing is written automatically.</strong> When a pattern is worth keeping you have two choices:
        <ul style={{margin:'6px 0 0 18px',padding:0}}>
          <li><strong>Promote as intent</strong> — when the combination represents a <em>new goal</em> the firm should name. Writes <code>/intents/&lt;slug&gt;.md</code>.</li>
          <li><strong>Promote as agent</strong> — when the combination is a <em>persistent runner</em> for an existing intent. Writes <code>/agents/&lt;slug&gt;.md</code> AND seeds a row in <code>learning_agents</code> so the agent appears in the Agents view (Public tab) immediately.</li>
        </ul>
        <strong>Auto-propose signal:</strong> rows highlighted in orange — chat matched <code>freelance</code> (no specific intent) <em>and</em> the chat-eval agent scored the turn as <code>solved</code> with high confidence. These are the strongest candidates for a new intent: the firm did something useful for which it has no documented goal.
      </div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {INTENT_TABS.map(t => (
          <span key={t.id} onClick={() => setTab(t.id)}
                className={`tag ${tab === t.id ? 'tag--orange' : ''}`}
                style={{cursor:'pointer'}}>
            {t.label}
          </span>
        ))}
      </div>
      {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
      {items === null && <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}
      {items && items.length === 0 && (
        <div style={{padding:20,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13,textAlign:'center'}}>
          No observations in this state.
        </div>
      )}
      <table className="data-table" style={{width:'100%'}}>
        {items && items.length > 0 && (
          <thead>
            <tr>
              <th>Skill combination</th>
              <th style={{width:80}}>Count</th>
              <th style={{width:120}} title="Intent slug the chat router matched. 'freelance' means nothing matched — strongest signal for a new intent.">Matched</th>
              <th>Sample message</th>
              <th style={{width:130}}>Last seen</th>
              <th style={{width:280}}></th>
            </tr>
          </thead>
        )}
        <tbody>
          {(items || []).map(obs => {
            const isAutoPropose = obs.matchedIntentSlug === 'freelance'
              && obs.lastEvalVerdict === 'yes'
              && (obs.lastEvalConfidence ?? 0) >= 0.8;
            return (
            <tr key={obs.patternKey} style={isAutoPropose ? {background:'rgba(255,100,40,0.06)'} : undefined}>
              <td style={{fontFamily:'JetBrains Mono',fontSize:12}}>{obs.skillIds.join(' + ')}</td>
              <td style={{fontFamily:'JetBrains Mono',fontSize:14,fontWeight:600}}>{obs.observationCount}</td>
              <td style={{fontFamily:'JetBrains Mono',fontSize:11.5,color: obs.matchedIntentSlug === 'freelance' ? 'var(--orange)' : 'var(--fg-muted)'}}>
                {obs.matchedIntentSlug || '—'}
              </td>
              <td style={{fontSize:12.5,fontStyle:'italic',color:'var(--fg-muted)'}}>{(obs.lastUserMessage || '').slice(0, 140) || '—'}</td>
              <td style={{fontSize:11.5,color:'var(--fg-muted)'}}>{new Date(obs.lastObservedAt).toLocaleString()}</td>
              <td>
                {obs.status === 'observed' && (
                  <div style={{display:'flex',gap:6,justifyContent:'flex-end',flexWrap:'wrap'}}>
                    <button className="btn-ghost" style={{padding:'4px 10px',fontSize:11}} onClick={() => dismiss(obs)} disabled={busy === obs.patternKey}>
                      Dismiss
                    </button>
                    <button className="btn-ghost" style={{padding:'4px 10px',fontSize:11}} onClick={() => promote(obs, 'intent')} disabled={busy === obs.patternKey} title="Write /intents/<slug>.md — name a new goal for the firm">
                      Promote as intent
                    </button>
                    <button className="btn-orange" style={{padding:'4px 10px',fontSize:11}} onClick={() => promote(obs, 'agent')} disabled={busy === obs.patternKey} title="Write /agents/<slug>.md AND seed a learning_agents row — a persistent runner for an existing intent">
                      Promote as agent
                    </button>
                  </div>
                )}
                {obs.status === 'promoted' && (obs.promotedPath || obs.promotedIntentPath) && (
                  <span style={{fontSize:11,color:'var(--support-green)'}}>
                    {obs.promotedKind === 'agent' ? 'agent' : 'intent'} → {obs.promotedPath || obs.promotedIntentPath}
                  </span>
                )}
                {obs.status === 'dismissed' && (
                  <span style={{fontSize:11,color:'var(--fg-muted)'}}>dismissed {obs.reviewedAt ? new Date(obs.reviewedAt).toLocaleDateString() : ''}</span>
                )}
                {isAutoPropose && obs.status === 'observed' && (
                  <div style={{fontSize:10.5,color:'var(--orange)',marginTop:4,fontWeight:600}}>
                    ★ Auto-propose: solved + high confidence + freelance
                  </div>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AdminAudit = () => {
  const [entries, setEntries] = React.useState(null);
  const [error, setError] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    listAuditEntries(100).then(setEntries).catch(err => setError(err.message));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const counts = React.useMemo(() => {
    if (!entries) return { total: 0, today: 0, high: 0, medium: 0 };
    const cutoff = Date.now() - 24 * 3600 * 1000;
    let today = 0, high = 0, medium = 0;
    for (const e of entries) {
      if (new Date(e.createdAt).getTime() > cutoff) today++;
      if (e.severity === 'high') high++;
      else if (e.severity === 'medium') medium++;
    }
    return { total: entries.length, today, high, medium };
  }, [entries]);

  return (
    <div>
      <QueueHealthPanel/>

      <div className="tile-grid" style={{marginBottom:24}}>
        <div className="kpi">
          <div className="kpi__label">Events (last 24h)</div>
          <div className="kpi__value">{entries === null ? '—' : counts.today}</div>
          <div className="kpi__sub">of {counts.total} shown</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">High severity</div>
          <div className="kpi__value">{entries === null ? '—' : counts.high}</div>
          <div className="kpi__sub">in current window</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Medium severity</div>
          <div className="kpi__value">{entries === null ? '—' : counts.medium}</div>
          <div className="kpi__sub">role changes, admin invites, /rules edits</div>
        </div>
      </div>

      <div className="section__head" style={{alignItems:'center'}}>
        <h2 style={{fontSize:18}}>Recent events</h2>
        <button className="btn-ghost" onClick={reload} style={{fontSize:12}}>Refresh</button>
      </div>

      {error && (
        <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>
      )}

      {entries === null && !error && (
        <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>
      )}

      {entries && entries.length === 0 && (
        <div style={{padding:20,color:'var(--fg-muted)',fontSize:13,background:'var(--bg-alt)',borderRadius:8}}>
          No events yet. Audit entries appear here as users register, sign in, issue invitations, change roles, edit OrgRepo, and spawn workers.
        </div>
      )}

      {entries && entries.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th style={{width:'14%'}}>When</th>
              <th style={{width:'20%'}}>Action</th>
              <th>Target</th>
              <th style={{width:'18%'}}>Actor</th>
              <th style={{width:'10%'}}>Severity</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => <AuditRow key={e.id} e={e}/>)}
          </tbody>
        </table>
      )}
    </div>
  );
};

/**
 * Queue health — firm-wide view of stuck/looping/auto-run agent work.
 * Surfaces what no per-user Workers page can: tasks owned by the
 * synthetic `cron:` user, stuck runs, and per-agent run counts.
 */
const QueueHealthPanel = () => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    getQueueHealth().then(setData).catch(err => setError(err.message));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const wc = data?.workerCounts || {};
  const issues = (data?.stuckRuns?.length || 0) + (data?.stuckWorkers?.length || 0);
  const orphanW = data?.orphanWorkers || [];
  const orphanR = data?.orphanRuns || [];
  const orphans = orphanW.length + orphanR.length;
  const alerts = data?.recentAlerts || [];

  return (
    <div style={{marginBottom:24,padding:16,background:'var(--bg-alt)',borderRadius:10}}>
      <div className="section__head" style={{alignItems:'center',marginTop:0}}>
        <h2 style={{fontSize:18}}>Queue health</h2>
        <div style={{flex:1}}/>
        {data?.runner && (
          <span style={{fontSize:11.5,fontWeight:600,color:data.runner.stale?'#A20235':'#00702A'}}>
            runner {data.runner.stale ? 'stale' : 'alive'}
          </span>
        )}
        <button className="btn-ghost" onClick={reload} style={{fontSize:12}}>Refresh</button>
      </div>

      <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:12}}>
        Every worker and agent run across the firm — including the background <code>cron:</code> runs no
        personal Workers page shows. Alerts fire automatically for stuck tasks, looping agents, and any
        manual agent that gets auto-run.
      </div>

      {error && (
        <div style={{padding:10,marginBottom:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>{error}</div>
      )}
      {data === null && !error && (
        <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>
      )}

      {data && (
        <>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
            {['running','queued','failed','done'].map(s => (
              <span key={s} style={{fontSize:11.5,padding:'3px 9px',borderRadius:6,background:'var(--bg-card)',border:'1px solid var(--border-soft, #e5ddd5)'}}>
                {s}: <strong>{wc[s] || 0}</strong>
              </span>
            ))}
            <span style={{fontSize:11.5,padding:'3px 9px',borderRadius:6,background:issues?'#FCE6E0':'var(--bg-card)',color:issues?'#A20235':'var(--fg)',border:'1px solid var(--border-soft, #e5ddd5)'}}>
              stuck: <strong>{issues}</strong>
            </span>
            <span style={{fontSize:11.5,padding:'3px 9px',borderRadius:6,background:orphans?'#FCE6E0':'var(--bg-card)',color:orphans?'#A20235':'var(--fg)',border:'1px solid var(--border-soft, #e5ddd5)'}}>
              orphaned: <strong>{orphans}</strong>
            </span>
          </div>

          {(data.stuckRuns?.length > 0 || data.stuckWorkers?.length > 0) && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Stuck now</div>
              {(data.stuckRuns || []).map(r => (
                <div key={r.id} style={{fontSize:12,color:'#A20235'}}>· run “{r.title}” — {r.status} {r.minutes} min</div>
              ))}
              {(data.stuckWorkers || []).map(w => (
                <div key={w.id} style={{fontSize:12,color:'#A20235'}}>· worker “{w.title}” — running {w.minutes} min</div>
              ))}
            </div>
          )}

          {orphans > 0 && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Orphaned (run/agent deleted — auto-recovered next tick)</div>
              {orphanR.map(r => (
                <div key={r.id} style={{fontSize:12,color:'#A20235'}}>· run “{r.title}” — {r.status}, owning agent deleted</div>
              ))}
              {orphanW.map(w => (
                <div key={w.id} style={{fontSize:12,color:'#A20235'}}>· worker “{w.title}” — {w.status}, parent run deleted</div>
              ))}
            </div>
          )}

          {data.topAgents24h?.length > 0 && (
            <div style={{marginBottom:4}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Runs per agent (24h)</div>
              <div style={{display:'flex',flexDirection:'column',gap:2}}>
                {data.topAgents24h.map(a => (
                  <div key={a.agentId} style={{fontSize:12,display:'flex',gap:8}}>
                    <span style={{flex:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.label}</span>
                    <span style={{color:'var(--fg-muted)'}}>{a.runs} run{a.runs===1?'':'s'}{a.cronRuns>0?` · ${a.cronRuns} auto`:''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {alerts.length > 0 && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Recent alerts (last 48h)</div>
              {alerts.slice(0,8).map(al => (
                <div key={al.id} style={{fontSize:12,color:'var(--fg-muted)'}}>
                  · <strong style={{color:'#A20235'}}>{al.kind}</strong> — {al.label}: {al.detail} <span style={{opacity:0.65}}>· {relativeTime(al.createdAt, { pastOnly: true })}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AuditRow = ({ e }) => {
  const sev = e.severity || 'info';
  const sevColor =
    sev === 'high'   ? { bg: '#FCE6E0', fg: '#A20235' } :
    sev === 'medium' ? { bg: '#FFE7DC', fg: '#B83C0F' } :
                       { bg: 'var(--bg-card)', fg: 'var(--fg-muted)' };
  return (
    <tr>
      <td style={{color:'var(--fg-muted)',fontSize:12}}>{relTime(e.createdAt)}</td>
      <td style={{fontFamily:'JetBrains Mono',fontSize:12}}>{e.action}</td>
      <td>{e.target ? <span><span style={{color:'var(--fg-muted)'}}>{e.target.type}: </span>{e.target.label || e.target.id}</span> : '—'}</td>
      <td>{e.actor.name}</td>
      <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:4,background:sevColor.bg,color:sevColor.fg,textTransform:'uppercase',letterSpacing:'0.04em'}}>{sev}</span></td>
    </tr>
  );
};

function relTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)  return 'just now';
  if (abs < hr)   return `${Math.round(abs / min)}m ago`;
  if (abs < day)  return `${Math.round(abs / hr)}h ago`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ago`;
  return d.toLocaleDateString();
}

/* ─── Generic taxonomy admin (Services / Industries / Clients) ──── */

const MdPickerModal = ({ currentPath, slug, onClose, onPick }) => {
  const [tree, setTree] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    getOrgRepoTree()
      .then(({ tree }) => setTree(tree || []))
      .catch(err => setError(err.message));
  }, []);

  const needle = q.trim().toLowerCase();
  const filtered = !tree ? [] : tree.filter(f =>
    f.path.endsWith('.md') && (!needle || f.path.toLowerCase().includes(needle)),
  );
  // Surface /roles/<slug>.md at the top if it exists (the canonical default).
  const defaultPath = `/roles/${slug}.md`;
  const filteredSorted = [...filtered].sort((a, b) => {
    if (a.path === defaultPath) return -1;
    if (b.path === defaultPath) return 1;
    return a.path.localeCompare(b.path);
  });

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:560,width:'95%',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>Connect MD file</div>
        <h3 style={{marginBottom:10}}>Pick a file from OrgRepo</h3>

        <input
          className="input"
          placeholder="Filter by path…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{marginBottom:12}}
          autoFocus
        />

        {error && <div style={{padding:10,marginBottom:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        {!tree && !error && <div style={{padding:14,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}

        {tree && filteredSorted.length === 0 && (
          <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:13,color:'var(--fg-muted)'}}>
            No markdown files match. Use OrgRepo's + button to create one first.
          </div>
        )}

        <div style={{flex:1,overflowY:'auto',marginTop:4}}>
          {filteredSorted.map(f => (
            <button
              type="button"
              key={f.path}
              onClick={() => onPick(f.path)}
              style={{
                width:'100%',padding:'8px 10px',marginBottom:4,
                display:'flex',alignItems:'center',gap:8,
                background: f.path === currentPath ? 'var(--bg-card)' : 'transparent',
                border:'1px solid ' + (f.path === currentPath ? 'var(--orange)' : 'var(--border)'),
                borderRadius:8,cursor:'pointer',textAlign:'left',
                fontFamily:'JetBrains Mono',fontSize:12,color:'var(--fg)',
              }}
            >
              <Icon name="file-text" size={14} stroke="var(--fg-muted)"/>
              <span style={{flex:1,wordBreak:'break-all'}}>{f.path}</span>
              {f.path === currentPath && <span style={{fontSize:10,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--orange)',fontWeight:600,fontFamily:'inherit'}}>current</span>}
              {f.path === defaultPath && f.path !== currentPath && (
                <span style={{fontSize:10,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,fontFamily:'inherit'}}>default</span>
              )}
            </button>
          ))}
        </div>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:10}}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const AdminTaxonomy = ({ title, empty, api, hasParent = false, loadParentList = null, parentField = 'parentServiceId', parentLabel = 'Parent service', requireParent = false, extraActions = null, renderLeadCell = null, extraFields = null, renderCreateExtras = null, afterCreate = null }) => {
  const [items, setItems] = React.useState(null);
  const [parents, setParents] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const reload = React.useCallback(async () => {
    setError(null);
    try {
      const list = await api.list();
      setItems(list);
      if (hasParent && !loadParentList) {
        // Same-list parents (top-level only). Use parentField so this
        // works for both Services (parentServiceId) and Companies
        // (parentCompanyId).
        setParents(list.filter(s => !s[parentField]).map(s => ({ id: s.id, name: s.name })));
      }
      if (loadParentList) setParents(await loadParentList());
    } catch (err) {
      setError(err.message);
    }
  }, [api, hasParent, loadParentList, parentField]);

  React.useEffect(() => { reload(); }, [reload]);

  const remove = async item => {
    if (!confirm(`Delete "${item.name}"? This also clears every user's pick of it.`)) return;
    try { await api.remove(item.id); await reload(); }
    catch (err) { setError(err.message); }
  };

  const parentName = id => parents.find(p => p.id === id)?.name || '—';

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
        <h2 style={{margin:0,fontFamily:'var(--font-display)',fontSize:22,fontWeight:400}}>{title}</h2>
        <button className="btn-orange" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14}/> Add {title.slice(0, -1).toLowerCase()}</button>
      </div>

      {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

      {items === null && <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>}

      {items && items.length === 0 && (
        <div style={{padding:20,color:'var(--fg-muted)',fontSize:13,background:'var(--bg-alt)',borderRadius:8}}>{empty}</div>
      )}

      {items && items.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{width:'18%'}}>Slug</th>
              {(hasParent || loadParentList) && <th style={{width:'18%'}}>{parentLabel}</th>}
              <th>Description</th>
              <th style={{width:130}}></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    {renderLeadCell && renderLeadCell(it)}
                    <strong>{it.name}</strong>
                  </div>
                </td>
                <td style={{fontFamily:'JetBrains Mono',fontSize:12,color:'var(--fg-muted)'}}>{it.slug}</td>
                {(hasParent || loadParentList) && (
                  <td style={{fontSize:12.5}}>{parentName(it[parentField] || it.parentServiceId || it.industryId || it.clientId)}</td>
                )}
                <td style={{fontSize:13,color:'var(--fg-muted)'}}>{it.description || '—'}</td>
                <td style={{display:'flex',gap:6,justifyContent:'flex-end',flexWrap:'wrap'}}>
                  {extraActions && extraActions(it, reload)}
                  <button className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5}} onClick={() => setEditing(it)}>Edit</button>
                  <button className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5,color:'var(--support-red)'}} onClick={() => remove(it)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createOpen && (
        <TaxonomyModal
          mode="create"
          title={title}
          parents={parents}
          hasParent={hasParent || !!loadParentList}
          parentField={parentField}
          parentLabel={parentLabel}
          requireParent={requireParent}
          extraFields={extraFields}
          headerExtras={renderCreateExtras}
          onClose={() => setCreateOpen(false)}
          onSave={async body => {
            const created = await api.create(body);
            if (afterCreate) {
              try { await afterCreate(created); } catch { /* surface from inside the modal */ }
            }
            setCreateOpen(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <TaxonomyModal
          key={editing.id}
          mode="edit"
          title={title}
          item={editing}
          parents={parents}
          hasParent={hasParent || !!loadParentList}
          parentField={parentField}
          parentLabel={parentLabel}
          requireParent={requireParent}
          extraFields={extraFields}
          onClose={() => setEditing(null)}
          onSave={async body => { await api.patch(editing.id, body); await reload(); }}
        />
      )}
    </div>
  );
};

const MultiChips = ({ value, options, onChange }) => {
  const selected = new Set(value || []);
  const toggle = id => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  if (!options || options.length === 0) {
    return <div style={{padding:'8px 10px',fontSize:12,color:'var(--fg-muted)',background:'var(--bg-card)',borderRadius:6}}>
      No options yet. Add one to the source list first.
    </div>;
  }
  return (
    <div style={{display:'flex',gap:6,flexWrap:'wrap',padding:'6px 0'}}>
      {options.map(o => {
        const on = selected.has(o.id);
        return (
          <button
            type="button"
            key={o.id}
            onClick={() => toggle(o.id)}
            style={{
              padding:'4px 10px',fontSize:12,borderRadius:14,cursor:'pointer',
              background: on ? 'var(--orange)' : 'var(--bg-card)',
              color: on ? '#fff' : 'var(--fg)',
              border: '1px solid ' + (on ? 'var(--orange)' : 'var(--border)'),
              fontWeight: on ? 600 : 400,
            }}
          >
            {on ? '✓ ' : '+ '}{o.name}
          </button>
        );
      })}
    </div>
  );
};

/** Build the initial `extras` bag for TaxonomyModal from an item +
 *  the extraFields render-prop. Multi-select fields default to [],
 *  others to ''. Pulled out of the modal so the open-time init and
 *  the item-changed sync (useEffect) share one source of truth. */
function buildExtrasInit(item, extraFields) {
  const init = {};
  const fields = (typeof extraFields === 'function' ? extraFields(item) : extraFields)?.fields || [];
  for (const f of fields) {
    if (f.type === 'multi') {
      init[f.key] = Array.isArray(item?.[f.key]) ? item[f.key] : [];
    } else {
      init[f.key] = (item && item[f.key] != null) ? item[f.key] : '';
    }
  }
  return init;
}

const TaxonomyModal = ({ mode, title, item, parents, hasParent, parentField, parentLabel, requireParent = false, extraFields = null, headerExtras = null, onClose, onSave }) => {
  const [name, setName] = React.useState(item?.name || '');
  const [description, setDescription] = React.useState(item?.description || '');
  const [parentId, setParentId] = React.useState(item?.[parentField] || item?.parentServiceId || item?.industryId || item?.clientId || '');
  // Extras live as a generic key→value bag. The extraFields render
  // prop supplies the JSX and reads/writes through these setters.
  const [extras, setExtras] = React.useState(() => buildExtrasInit(item, extraFields));
  const setExtra = (key, value) => setExtras(prev => ({ ...prev, [key]: value }));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Snapshot of the last-persisted values, used to drive auto-save dirtiness
  // in edit mode without depending on the (stale-after-reload) `item` prop.
  const [baselineSig, setBaselineSig] = React.useState(null);

  const parentOf = it => it?.[parentField] || it?.parentServiceId || it?.industryId || it?.clientId || '';

  // Defensive sync: when the `item` prop changes (different row
  // selected in the list, or admin re-opened edit after the
  // refresh effect fired), re-seed every form field from the new
  // item. useState initializers only run on FIRST mount; without
  // this effect, switching from row A to row B without unmount
  // would keep the form showing row A. The hooks-order refactor
  // means the modal unmount/remount cleanly between opens, so this
  // is belt-and-braces — fires when item.id changes only.
  React.useEffect(() => {
    setName(item?.name || '');
    setDescription(item?.description || '');
    setParentId(parentOf(item));
    setExtras(buildExtrasInit(item, extraFields));
    setError(null);
    setBaselineSig(JSON.stringify({
      name: (item?.name || '').trim(),
      description: (item?.description || '').trim(),
      parentId: parentOf(item),
      extras: buildExtrasInit(item, extraFields),
    }));
  }, [item?.id, parentField, extraFields]);

  const currentSig = JSON.stringify({
    name: name.trim(),
    description: description.trim(),
    parentId: parentId || '',
    extras,
  });
  const dirty = mode === 'edit' && baselineSig !== null && currentSig !== baselineSig;

  // Auto-save (edit mode only): persist on pause without closing the modal.
  // Invalid states throw so they surface as a failed-save rather than
  // silently wiping a required field.
  const persist = async () => {
    if (!name.trim()) throw new Error('Name is required');
    if (requireParent && !parentId) throw new Error(`${parentLabel} is required`);
    const body = { name: name.trim(), description: description.trim() || null, ...extras };
    for (const k of Object.keys(extras)) if (body[k] === '') body[k] = null;
    if (hasParent) body[parentField] = parentId || null;
    await onSave(body);
    setBaselineSig(currentSig);
  };

  const { status: saveStatus, error: saveError, saveNow } = useAutoSave({
    dirty,
    save: persist,
    enabled: mode === 'edit',
    signature: currentSig,
  });

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    if (requireParent && !parentId) { setError(`${parentLabel} is required`); return; }
    setBusy(true); setError(null);
    try {
      const body = { name: name.trim(), description: description.trim() || null, ...extras };
      // Normalise empty-string extras to null so the server sees an
      // explicit clear instead of an empty string FK. Arrays pass
      // through untouched (multi-select fields).
      for (const k of Object.keys(extras)) if (body[k] === '') body[k] = null;
      if (hasParent) body[parentField] = parentId || null;
      await onSave(body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const extraSpec = typeof extraFields === 'function' ? extraFields(item) : extraFields;

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:520}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>
          {mode === 'create' ? `New ${title.slice(0, -1).toLowerCase()}` : `Edit ${title.slice(0, -1).toLowerCase()}`}
        </div>
        <h3>{mode === 'create' ? `Add to ${title}.` : `Edit ${item?.name}.`}</h3>

        <form onSubmit={mode === 'create' ? submit : (e => { e.preventDefault(); saveNow(); })}>
          <div className="field"><label>Name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} autoFocus maxLength={80}/>
          </div>
          {hasParent && (
            <div className="field"><label>{parentLabel}{requireParent ? '' : ' (optional)'}</label>
              <select className="select" value={parentId} onChange={e => setParentId(e.target.value)}>
                <option value="">{requireParent ? `— choose a ${parentLabel.toLowerCase()} —` : '— none —'}</option>
                {parents.filter(p => p.id !== item?.id).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field"><label>Description (optional, one line)</label>
            <textarea className="textarea" rows={2} value={description} onChange={e => setDescription(e.target.value)} maxLength={280}/>
          </div>

          {mode === 'create' && headerExtras && (
            <div className="field">
              {headerExtras()}
            </div>
          )}

          {extraSpec?.fields?.map(f => (
            <div className="field" key={f.key}>
              <label>{f.label}{f.required ? '' : ' (optional)'}</label>
              {f.type === 'multi' ? (
                <MultiChips
                  value={Array.isArray(extras[f.key]) ? extras[f.key] : []}
                  options={f.options}
                  onChange={ids => setExtra(f.key, ids)}
                />
              ) : f.type === 'select' ? (
                <select className="select" value={extras[f.key] || ''} onChange={e => setExtra(f.key, e.target.value)}>
                  <option value="">— none —</option>
                  {f.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              ) : (
                <input className="input" value={extras[f.key] || ''} onChange={e => setExtra(f.key, e.target.value)}/>
              )}
            </div>
          ))}

          {error && <div style={{marginBottom:12,padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',alignItems:'center'}}>
            {mode === 'create' ? (
              <>
                <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn-orange" disabled={busy || !name.trim()}>
                  {busy ? 'Saving…' : 'Create'}
                </button>
              </>
            ) : (
              <>
                <SaveStatus status={saveStatus} error={saveError} dirty={dirty}/>
                <button type="button" className="btn-orange" onClick={() => { saveNow(); onClose(); }}>Done</button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

/* Per-kind config for AddEntityButton. Mirrors what the old Admin
 * tabs (Services / Industries / Clients / Projects / Organizations)
 * fed into AdminTaxonomy, minus the list/edit/delete management —
 * those entities are now created straight from their directory page
 * and edited inline on the detail pages. */
const ADD_ENTITY_KINDS = {
  services: {
    title: 'Services',
    api: adminServices,
    parentField: 'parentServiceId',
    parentLabel: 'Parent service',
    loadParents: async () => (await adminServices.list()).filter(s => !s.parentServiceId).map(s => ({ id: s.id, name: s.name })),
  },
  industries: {
    title: 'Industries',
    api: adminIndustries,
  },
  organizations: {
    title: 'Organizations',
    api: adminCompanies,
    parentField: 'parentCompanyId',
    parentLabel: 'Parent organization',
    loadParents: async () => (await adminCompanies.list()).filter(c => !c.parentCompanyId).map(c => ({ id: c.id, name: c.name })),
  },
  clients: {
    title: 'Clients',
    api: adminClients,
    parentField: 'industryId',
    parentLabel: 'Industry',
    loadParents: async () => (await adminIndustries.list()).map(i => ({ id: i.id, name: i.name })),
    withLogo: true,
  },
  projects: {
    title: 'Projects',
    api: adminProjects,
    parentField: 'clientId',
    parentLabel: 'Client',
    requireParent: true,
    loadParents: async () => (await adminClients.list()).map(c => ({ id: c.id, name: c.name })),
    withProjectExtras: true,
  },
};

/* Stand-alone "Add <entity>" trigger for the directory list pages.
 * Reuses the exact TaxonomyModal create popup that used to live in the
 * Admin panel. Render it admin-gated — the create endpoints are
 * admin-only on the server. `onCreated` should refresh the caller's list. */
export const AddEntityButton = ({ kind, onCreated, style = null, initialName = '', label = null }) => {
  const cfg = ADD_ENTITY_KINDS[kind];
  const [open, setOpen] = React.useState(false);
  const [parents, setParents] = React.useState([]);
  const [projOptions, setProjOptions] = React.useState({ services: [], industries: [] });
  const pendingLogoRef = React.useRef(null);
  const [logoPreview, setLogoPreview] = React.useState(null);

  if (!cfg) return null;

  const clearLogo = () => {
    pendingLogoRef.current = null;
    setLogoPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
  };

  const onPickLogo = e => {
    const f = e.target.files?.[0] || null;
    pendingLogoRef.current = f;
    setLogoPreview(prev => { if (prev) URL.revokeObjectURL(prev); return f ? URL.createObjectURL(f) : null; });
  };

  const openModal = () => {
    setOpen(true);
    if (cfg.loadParents) cfg.loadParents().then(setParents).catch(() => setParents([]));
    if (cfg.withProjectExtras) {
      Promise.all([adminServices.list(), adminIndustries.list()])
        .then(([services, industries]) => setProjOptions({ services, industries }))
        .catch(() => { /* pickers degrade to "— none —" */ });
    }
  };

  const close = () => { setOpen(false); clearLogo(); };

  return (
    <>
      <button className="btn-orange" style={style || undefined} onClick={openModal}>
        <Icon name="plus" size={14}/> {label || `Add ${cfg.title.slice(0, -1).toLowerCase()}`}
      </button>
      {open && (
        <TaxonomyModal
          mode="create"
          title={cfg.title}
          item={initialName ? { name: initialName } : undefined}
          parents={parents}
          hasParent={!!cfg.loadParents}
          parentField={cfg.parentField || 'parentServiceId'}
          parentLabel={cfg.parentLabel || 'Parent'}
          requireParent={!!cfg.requireParent}
          extraFields={cfg.withProjectExtras ? () => ({
            fields: [
              { key: 'serviceIds',  label: 'Core services',  type: 'multi',
                options: (projOptions.services  || []).map(s => ({ id: s.id, name: s.name })) },
              { key: 'industryIds', label: 'Core industries', type: 'multi',
                options: (projOptions.industries || []).map(i => ({ id: i.id, name: i.name })) },
            ],
          }) : null}
          headerExtras={cfg.withLogo ? () => (
            <>
              <label>Logo (optional)</label>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                {logoPreview
                  ? <img src={logoPreview} alt="" style={{width:48,height:48,objectFit:'contain',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:6}}/>
                  : <div style={{width:48,height:48,border:'1px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-muted)',fontSize:11}}>logo</div>}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={onPickLogo}
                  style={{fontSize:12}}
                />
              </div>
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:6}}>Uploads right after the client is created. You can replace or clear it later on its profile.</div>
            </>
          ) : null}
          onClose={close}
          onSave={async body => {
            const created = await cfg.api.create(body);
            if (cfg.withLogo && pendingLogoRef.current) {
              const clientId = created?.id || created?._id;
              if (clientId) {
                try { await uploadClientLogo(clientId, pendingLogoRef.current); }
                catch { /* logo is optional — the client was still created */ }
              }
            }
            setOpen(false);
            clearLogo();
            onCreated?.(created);
          }}
        />
      )}
    </>
  );
};

/* ─── Admin Logs ──────────────────────────────────────────────────
 * Firm-wide audit trail. Same shape as My Logs, but pulls from
 * /api/admin/audit (admin only) and shows the actor column too.
 */
export const AdminLogsView = () => {
  const [entries, setEntries] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [limit, setLimit] = React.useState(200);

  const reload = React.useCallback(() => {
    setError(null);
    listAuditEntries(limit)
      .then(setEntries)
      .catch(err => setError(err.message));
  }, [limit]);

  React.useEffect(() => { reload(); }, [reload]);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Admin Logs</h1>
        <div className="lede">Every audited action across the firm — every user, every agent, every skill, every OrgRepo edit. Most recent first. Use Refresh to pull the latest; bump the limit for deeper history.</div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
        {entries && <span style={{fontSize:12,color:'var(--fg-muted)'}}>{entries.length} entries</span>}
        <div style={{flex:1}}/>
        <select className="select" value={limit} onChange={e => setLimit(Number(e.target.value))} style={{padding:'4px 8px',fontSize:12}}>
          <option value={100}>100</option>
          <option value={200}>200</option>
          <option value={500}>500</option>
          <option value={1000}>1000</option>
        </select>
        <button className="btn-ghost" onClick={reload} style={{fontSize:12,padding:'4px 10px'}}>Refresh</button>
      </div>

      {error && <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13,marginBottom:14}}>{error}</div>}

      {entries === null && !error && (
        <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>
      )}

      {entries && entries.length === 0 && (
        <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:13,color:'var(--fg-muted)'}}>
          No audit entries yet.
        </div>
      )}

      {entries && entries.length > 0 && (
        <table className="tbl">
          <thead><tr>
            <th style={{width:140}}>When</th>
            <th style={{width:140}}>Actor</th>
            <th style={{width:220}}>Action</th>
            <th>Target</th>
            <th style={{width:80}}>Severity</th>
          </tr></thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td style={{color:'var(--fg-muted)',fontSize:12}}>{adminRelTime(e.createdAt)}</td>
                <td style={{fontSize:12}}>{e.actor?.name || e.actor?.id || '—'}</td>
                <td style={{fontFamily:'JetBrains Mono',fontSize:12}}>{e.action}</td>
                <td>{e.target
                  ? <span><span style={{color:'var(--fg-muted)',fontSize:11}}>{e.target.type}: </span>{e.target.label || e.target.id || '—'}</span>
                  : '—'}</td>
                <td>
                  <span style={{
                    fontSize:10.5,padding:'2px 7px',borderRadius:4,fontWeight:600,
                    letterSpacing:'0.04em',textTransform:'uppercase',
                    background: e.severity === 'high' ? '#FCE6E0' : e.severity === 'medium' ? '#FFE7DC' : 'var(--bg-card)',
                    color: e.severity === 'high' ? '#A20235' : e.severity === 'medium' ? '#B83C0F' : 'var(--fg-muted)',
                  }}>{e.severity || 'info'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

function adminRelTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)     return 'just now';
  if (abs < hr)      return `${Math.round(abs / min)}m ago`;
  if (abs < day)     return `${Math.round(abs / hr)}h ago`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ago`;
  return d.toLocaleString();
}

/* ─── README view (admin-only) ────────────────────────────────────
 * Renders the canonical README.md from the repo root (imported via
 * Vite's ?raw suffix at the top of this file). Editing README.md
 * auto-syncs the in-app view on the next build.
 *
 * The header strip carries three versions — the manual's own commit
 * (last commit that touched README.md), the underlying build commit,
 * and today's date — plus three toolbar actions:
 *   - Save as PDF (window.print() + .manual-print stylesheet).
 *   - Check for updates (lists commits since the manual's last update
 *     that didn't touch README.md — likely-undocumented changes).
 *   - AI audit (drops a pre-filled audit prompt into a fresh chat).
 *
 * The .manual-print CSS in styles.css hides the chrome (shell, header,
 * toolbar) at print time so the saved PDF is just the prose + the
 * version strip (kept visible because the print needs to carry it).
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

const AUDIT_PROMPT =
  "Audit the Manual (README.md) against the current source. Walk the repo — `functions/`, `src/`, `worker/`, `orgrepo/`, `migrations/`, `scripts/` — and produce a punch list of:\n" +
  "1. Features / endpoints / skills / agents / D1 tables shipped in code but **missing from the Manual**.\n" +
  "2. Things the Manual claims that the code **no longer does** (stale prose).\n" +
  "3. Sub-sections that should be **refined, condensed, or split** for clarity.\n" +
  "4. Sub-sections or paragraphs that should be **deleted** because the underlying feature is gone.\n\n" +
  "For each item, cite the file/line in code and propose the exact README diff (before → after). Build on the current structure (don't rewrite §17/§18 wholesale). Group findings by README section. End with a short prioritised plan.";

export const OnboardingView = () => {
  const [meta, setMeta] = React.useState(null);
  const [metaErr, setMetaErr] = React.useState(null);
  const [showUpdates, setShowUpdates] = React.useState(false);
  React.useEffect(() => {
    fetch('/manual-meta.json', { cache: 'no-cache' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`manual-meta → ${r.status}`)))
      .then(setMeta)
      .catch(e => setMetaErr(e.message));
  }, []);

  // Build sha + time are baked into the bundle by vite (see vite.config.ts)
  // and re-exported from shell.jsx for any view that wants to display them.
  const buildSha  = String(BUILD_SHA  || 'unknown').slice(0, 7);
  const buildTime = String(BUILD_TIME || '');

  const savePdf = () => {
    // Server-rendered, well-formatted PDF — the same pipeline as the
    // slide-deck export: open the branded export shell, which shows a
    // loading animation while the endpoint renders a real cover +
    // paginated A4 document with the mermaid diagrams drawn, then
    // downloads it. When Browser Rendering is unavailable the endpoint
    // serves a print-ready page instead. (Same shell pattern as the
    // memo / deck "Export PDF" links.)
    window.open(
      exportShellUrl({ src: '/api/manual/export/pdf', kind: 'pdf', title: 'The-Agentic-Organization' }),
      '_blank',
    );
  };

  const [mdBusy, setMdBusy] = React.useState(false);
  const saveMarkdown = async () => {
    // The whole eOS as one Markdown file: the manual source with every
    // diagram + figure rewritten into words (Markdown has no graphics),
    // for onboarding hand-offs and feeding the model. Generation is
    // instant, so we just fetch + trigger a blob download in place rather
    // than routing through the branded export shell the PDF uses.
    if (mdBusy) return;
    setMdBusy(true);
    try {
      const r = await fetch('/api/manual/export/markdown', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) throw new Error(`export → ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'The-Agentic-Organization.md';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(`Markdown export failed: ${e.message || e}`);
    } finally {
      setMdBusy(false);
    }
  };

  const openAuditChat = () => {
    try { sessionStorage.setItem('erasphere.chatSeed', AUDIT_PROMPT); } catch {}
    location.hash = 'chats';
  };

  const undocumented = meta?.commitsSinceManual || [];

  return (
    <div className="page manual-page">
      <div className="page__head">
        <h1>The Manual</h1>
        <div className="lede">The mission, the mental model, the platform surface for users and admins, and how every artefact is produced. Open this on day one and any time the model evolves.</div>
      </div>

      <div className="manual-version-strip">
        <div className="manual-version-strip__cells">
          <div className="manual-version-strip__cell">
            <div className="manual-version-strip__label">Manual version</div>
            <div className="manual-version-strip__value">
              <code>{meta?.manualSha || '…'}</code>
              <span className="manual-version-strip__sub">updated {fmtDate(meta?.manualUpdatedAt)}</span>
            </div>
          </div>
          <div className="manual-version-strip__cell">
            <div className="manual-version-strip__label">Build version</div>
            <div className="manual-version-strip__value">
              <code>{buildSha}</code>
              <span className="manual-version-strip__sub">built {fmtDate(buildTime)}</span>
            </div>
          </div>
          <div className="manual-version-strip__cell">
            <div className="manual-version-strip__label">Today</div>
            <div className="manual-version-strip__value">
              <code>{new Date().toISOString().slice(0, 10)}</code>
              <span className="manual-version-strip__sub">{fmtDate(new Date().toISOString())}</span>
            </div>
          </div>
        </div>
        <div className="manual-version-strip__actions">
          <button type="button" className="btn-ghost" onClick={savePdf} title="Render this manual as a branded, print-ready PDF (server-rendered — same as the slide-deck export)">Save as PDF</button>
          <button type="button" className="btn-ghost" onClick={saveMarkdown} disabled={mdBusy} title="Download the whole eOS as one Markdown file — every diagram and figure rewritten into words, for onboarding hand-offs or feeding the model">{mdBusy ? 'Preparing…' : 'Download Markdown'}</button>
          <button type="button" className="btn-ghost" onClick={() => setShowUpdates(s => !s)}>
            {showUpdates ? 'Hide updates' : `Check for updates${meta ? ` (${meta.undocumentedCount})` : ''}`}
          </button>
          <button type="button" className="btn-orange" onClick={openAuditChat} title="Ask the assistant to audit the source against the Manual and propose specific edits">AI audit</button>
        </div>
        {metaErr && <div className="manual-version-strip__error">Manual metadata unavailable: {metaErr}</div>}
      </div>

      {showUpdates && (
        <div className="manual-updates">
          <div className="manual-updates__head">
            <strong>{undocumented.length}</strong> commit{undocumented.length === 1 ? '' : 's'} since the Manual was last updated{meta?.manualSha ? <> ({meta.manualSha} · {fmtDate(meta.manualUpdatedAt)})</> : null} that did not touch <code>README.md</code>. These are candidates for documentation — run the AI audit to triage them.
          </div>
          {undocumented.length === 0
            ? <div className="manual-updates__empty">Nothing to flag — the Manual is in sync with the current build.</div>
            : (
              <ul className="manual-updates__list">
                {undocumented.map(c => (
                  <li key={c.sha}>
                    <code>{c.sha}</code>
                    <span className="manual-updates__when">{fmtDate(c.date)}</span>
                    <span className="manual-updates__subject">{c.subject}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}

      <div className="md-body manual-body">
        <MarkdownView source={onboardingMd} allowHtml/>
      </div>
    </div>
  );
};

/* ─── AI Models dashboard ──────────────────────────────────────
   Lists every Claude call site in the system with its effective
   model + max-tokens (resolved from ai_model_overrides + the
   code defaults in ai-registry.ts) and aggregated token usage +
   USD cost for the last 24h / 7d / 30d.
   Admins can change the model or max-tokens per call site
   without a deploy. */
async function loadAiModels() {
  const res = await fetch('/api/admin/ai-models', { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `ai-models → ${res.status}`);
  return j;
}

/* ─── Deck slides: version timeline + design rules / optimizations ─── */

async function loadDeckSlides() {
  const res = await fetch('/api/admin/deck-slides', { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck-slides → ${res.status}`);
  return j;
}
async function loadDeckTimeline(runId) {
  const res = await fetch(`/api/admin/deck-slides?runId=${encodeURIComponent(runId)}`, { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck-slides → ${res.status}`);
  return j;
}
async function loadVersionHtml(versionId) {
  const res = await fetch(`/api/admin/deck-slides?versionId=${encodeURIComponent(versionId)}`, { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck-slides → ${res.status}`);
  return j.html || '';
}
async function manageLearningReq(learningId, action) {
  const res = await fetch('/api/admin/deck-slides', {
    method: 'PATCH', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ learningId, action }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `deck-slides → ${res.status}`);
  return j;
}
async function probeBindingReq() {
  const res = await fetch('/api/admin/deck-vision-probe', { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `probe → ${res.status}`);
  return j;
}


const AdminDeckSlides = () => {
  const [data, setData] = React.useState(null);        // { decks, learnings }
  const [error, setError] = React.useState(null);
  const [sel, setSel] = React.useState(null);          // selected run_id
  const [deck, setDeck] = React.useState(null);        // { slides }
  const [deckBusy, setDeckBusy] = React.useState(false);
  const [previews, setPreviews] = React.useState({});  // versionId -> html
  const [openV, setOpenV] = React.useState(null);      // versionId currently previewed
  const [openFb, setOpenFb] = React.useState({});      // versionId -> expand feedback
  const [probe, setProbe] = React.useState(null);
  const [probing, setProbing] = React.useState(false);
  const [busyL, setBusyL] = React.useState(null);

  const reload = React.useCallback(() => { setError(null); loadDeckSlides().then(setData).catch(e => setError(e.message)); }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const openDeck = async (runId) => {
    setSel(runId); setDeck(null); setDeckBusy(true); setError(null); setOpenV(null);
    try { setDeck(await loadDeckTimeline(runId)); }
    catch (e) { setError(e.message); }
    finally { setDeckBusy(false); }
  };
  const preview = async (versionId) => {
    if (openV === versionId) { setOpenV(null); return; }
    setOpenV(versionId);
    if (previews[versionId] === undefined) {
      try { const html = await loadVersionHtml(versionId); setPreviews(p => ({ ...p, [versionId]: html })); }
      catch (e) { setError(e.message); }
    }
  };
  const runProbe = async () => { setProbing(true); try { setProbe(await probeBindingReq()); } catch (e) { setError(e.message); } finally { setProbing(false); } };
  const learn = async (id, action) => { setBusyL(id); try { await manageLearningReq(id, action); reload(); } catch (e) { setError(e.message); } finally { setBusyL(null); } };

  const badge = (t, c) => <span style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',padding:'2px 7px',borderRadius:5,background:c,color:'#fff'}}>{t}</span>;
  const kindColor = (k) => k === 'archetype_change' ? '#B83C0F' : k === 'in_place' ? '#2E7D46' : '#888';
  const lifeColor = (l) => l === 'regenerated' ? '#7A3FB8' : l === 'generated' ? '#2E7D46' : '#999';

  if (error && !data) return <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>Deck slides load failed: {error}</div>;
  if (!data) return <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>Loading decks…</div>;

  const bindingCard = (
    <div style={{border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px',marginBottom:20,background:'var(--bg-alt)'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <div style={{fontWeight:600,fontSize:14}}>Browser-Rendering binding (pixel geometry check)</div>
        {probe && badge(probe.working ? 'working' : (probe.binding && probe.binding.present ? 'not working' : 'absent'), probe.working ? '#00702A' : '#A20235')}
        <button type="button" className="btn-ghost" style={{padding:'5px 12px',fontSize:13,marginLeft:'auto'}} disabled={probing} onClick={runProbe}>{probing ? 'Testing…' : 'Test binding'}</button>
      </div>
      <div style={{fontSize:12.5,color:'var(--fg-muted)',marginTop:6}}>
        {probe ? probe.detail : 'Checks whether this environment can actually launch headless Chromium and measure real slide geometry. The HTML self-check always runs; this is the extra pixel-accurate layer that activates the “binding” vision mode.'}
      </div>
      {probe && <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:4}}>mode: <b>{probe.adminSetting && probe.adminSetting['deck-vision-mode']}</b> · present {String(probe.binding && probe.binding.present)} · launched {String(probe.binding && probe.binding.launched)} · measured {String(probe.binding && probe.binding.measured)}</div>}
    </div>
  );

  // ── Single-deck timeline view ──
  if (sel) {
    return (
      <div>
        {bindingCard}
        <button type="button" className="btn-ghost" style={{padding:'5px 12px',fontSize:13,marginBottom:14}} onClick={() => { setSel(null); setDeck(null); setOpenV(null); }}>← All decks</button>
        {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>{error}</div>}
        {deckBusy && <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>Loading timeline…</div>}
        {deck && !deck.slides.length && <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>No versions captured for this deck yet.</div>}
        {deck && deck.slides.map(s => (
          <div key={s.slide_index} style={{marginBottom:22,border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
              <div style={{fontWeight:600,fontSize:14}}>Slide {s.slide_index}{s.title ? ` — ${s.title}` : ''}</div>
              {s.live.approved && badge('approved', '#00702A')}
              {s.live.reviewed && badge('reviewed', '#555')}
              {s.live.render_status && badge(s.live.render_status, '#888')}
              <span style={{fontSize:11.5,color:'var(--fg-muted)',marginLeft:'auto'}}>{s.versions.length} version{s.versions.length === 1 ? '' : 's'} — left → right is oldest → newest</span>
            </div>
            <div style={{display:'flex',gap:10,overflowX:'auto',paddingBottom:6}}>
              {s.versions.map(v => {
                const issues = (v.check_issues && ((v.check_issues.geometry || []).length + (v.check_issues.selfCheck || []).length)) || 0;
                return (
                  <div key={v.id} style={{minWidth:236,maxWidth:264,flexShrink:0,border:'1px solid var(--border)',borderRadius:10,padding:'10px 12px',background: v.redesign_kind === 'archetype_change' ? '#FFF3EC' : 'var(--bg)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6,flexWrap:'wrap'}}>
                      <span style={{fontWeight:700,fontSize:13}}>v{v.version_no}</span>
                      {badge(v.lifecycle, lifeColor(v.lifecycle))}
                      {v.approval === 'approved' && badge('approved', '#00702A')}
                    </div>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:6}}>
                      {v.trigger && badge(String(v.trigger).replace('_', ' '), '#41566B')}
                      {v.redesign_kind && v.redesign_kind !== 'initial' && badge(String(v.redesign_kind).replace('_', ' '), kindColor(v.redesign_kind))}
                    </div>
                    {v.redesign_kind === 'archetype_change'
                      ? <div style={{fontSize:11,color:'#B83C0F',marginBottom:6}}>type changed: {v.prev_layout || '?'} → {v.layout || '?'}</div>
                      : v.layout ? <div style={{fontSize:11,color:'var(--fg-muted)',marginBottom:6}}>{v.layout}</div> : null}
                    {v.feedback && (
                      <div style={{fontSize:11.5,color:'var(--fg)',marginBottom:6,whiteSpace:'pre-wrap'}}>
                        {openFb[v.id] ? v.feedback : (v.feedback.length > 110 ? v.feedback.slice(0, 110) + '…' : v.feedback)}
                        {v.feedback.length > 110 && <span style={{color:'var(--orange)',cursor:'pointer'}} onClick={() => setOpenFb(f => ({ ...f, [v.id]: !f[v.id] }))}> {openFb[v.id] ? 'less' : 'more'}</span>}
                      </div>
                    )}
                    {issues > 0 && <div style={{fontSize:11,color:'#A20235',marginBottom:6}}>⚠ {issues} QA issue{issues === 1 ? '' : 's'} found at this version</div>}
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>{v.created_by_name || 'system'} · {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                      {v.has_html && <button type="button" className="btn-ghost" style={{padding:'3px 10px',fontSize:12,marginLeft:'auto'}} onClick={() => preview(v.id)}>{openV === v.id ? 'Hide' : 'Preview'}</button>}
                    </div>
                    {openV === v.id && (
                      <div style={{marginTop:8,width:'100%',height:140,overflow:'hidden',border:'1px solid var(--border)',borderRadius:6,background:'#fff'}}>
                        {previews[v.id] === undefined
                          ? <div style={{fontSize:12,color:'var(--fg-muted)',padding:8}}>Loading preview…</div>
                          : <iframe title={`v${v.version_no}`} srcDoc={previews[v.id]} sandbox="allow-same-origin" scrolling="no" style={{width:1920,height:1080,border:'none',transform:'scale(0.122)',transformOrigin:'top left',pointerEvents:'none'}} />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Deck list + optimizations ──
  const decks = data.decks || [];
  const learnings = data.learnings || [];
  return (
    <div>
      <div className="lede" style={{marginBottom:16}}>Every generated and re-generated slide is saved here as a timeline. Open a deck to step through each slide's versions in order — the change that was asked for, the optimization applied, whether it kept the slide type (a <b>type change</b> is flagged in orange, and should stay rare) — preview any version, and see the QA findings. Manage the accumulated optimizations below.</div>
      {bindingCard}
      {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>{error}</div>}

      <div className="section__head"><h2 style={{fontSize:'var(--text-xl)'}}>Decks</h2></div>
      {!decks.length && <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>No deck timelines yet — they fill in as slides are generated and regenerated.</div>}
      {!!decks.length && (
        <table className="tbl" style={{marginBottom:28}}>
          <thead><tr><th>Deck</th><th>Owner</th><th>Slides</th><th>Versions</th><th>Regens</th><th>Type changes</th><th>Approved</th><th>Last activity</th></tr></thead>
          <tbody>
            {decks.map(d => (
              <tr key={d.run_id} style={{cursor:'pointer'}} onClick={() => openDeck(d.run_id)}>
                <td><span style={{color:'var(--orange)',fontWeight:500}}>{d.title}</span>{d.kind === 'chat' && <span style={{fontSize:10.5,color:'var(--fg-muted)'}}> · chat</span>}</td>
                <td>{d.owner || '—'}</td>
                <td>{d.slides}</td>
                <td>{d.versions}</td>
                <td>{d.regenerated}</td>
                <td>{d.type_changes > 0 ? <span style={{color:'#B83C0F',fontWeight:600}}>{d.type_changes}</span> : '0'}</td>
                <td>{d.approved == null ? '—' : `${d.approved}/${d.total}`}</td>
                <td style={{color:'var(--fg-muted)'}}>{d.last_at ? new Date(d.last_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="section__head"><h2 style={{fontSize:'var(--text-xl)'}}>Design rules &amp; optimizations (deck learnings)</h2></div>
      <div className="lede" style={{marginBottom:12}}>The single place to review and approve what the deck builder has learned (`deck_learnings`) — distilled from reviewer critiques and your feedback on regenerations. <b>Global</b> applies to everyone, <b>user</b> is one person's taste. <b>Active</b> rules feed every future build; <b>Promote</b> a candidate to approve it now, or <b>Retire</b> one that shouldn't apply.</div>
      {!learnings.length && <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>No optimizations captured yet — they accumulate as decks are reviewed and as you give feedback on slides.</div>}
      {learnings.map(r => (
        <div key={r.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',marginBottom:8,border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-alt)'}}>
          <div style={{flex:1}}>
            <div style={{fontSize:14,marginBottom:5}}>{r.rule}</div>
            <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
              {badge(r.scope === 'global' ? 'global' : 'user', r.scope === 'global' ? '#00702A' : '#B83C0F')}
              {badge(r.stage, '#555')}
              {badge(r.status, r.status === 'active' ? '#202020' : '#93867D')}
              <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>{r.source} · seen {r.evidence_count}×</span>
            </div>
          </div>
          {r.status !== 'active' && <button type="button" onClick={() => learn(r.id, 'promote')} disabled={busyL === r.id} className="btn-ghost" style={{padding:'6px 12px',fontSize:13}}>{busyL === r.id ? '…' : 'Promote'}</button>}
          <button type="button" onClick={() => learn(r.id, 'retire')} disabled={busyL === r.id} className="btn-ghost" style={{padding:'6px 12px',fontSize:13}}>{busyL === r.id ? '…' : 'Retire'}</button>
        </div>
      ))}
    </div>
  );
};

/* ─── Operational settings (admin_settings key/value) ──────────── */

async function loadAdminSettings() {
  const res = await fetch('/api/admin/settings', { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `settings → ${res.status}`);
  return j;
}
async function saveAdminSetting(key, value) {
  const res = await fetch('/api/admin/settings', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ key, value }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `settings → ${res.status}`);
  return j;
}

/* Agent image style picker — the catalogue gallery (/api/style-gallery)
 * embedded so an admin can click any of the 20 styles to set the firm-wide
 * agent-image standard. The gallery is the single picker implementation
 * (also reachable directly); clicking a tile PATCHes the agent-image-style
 * admin setting.
 *
 * Plus a bulk "regenerate all agent images in the current style" action:
 * changing the standard never retroactively re-skins existing agents (and
 * migration-seeded agents like the slide-deck creator / proposal builder were
 * never given an image at all), so this re-runs generation for every firm
 * agent — one paid gpt-image-1 call each, sequential, with progress. Agents
 * with a manually UPLOADED image are skipped so a deliberate upload isn't lost. */
const AdminAgentImages = () => {
  const [agents, setAgents] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [prog, setProg] = React.useState(null);   // { done, total, current, ok, fails: [{label,msg}] }
  const [err, setErr] = React.useState(null);
  const abortRef = React.useRef(false);
  const galleryRef = React.useRef(null);

  React.useEffect(() => {
    listAgents().then(r => setAgents(r.agents || [])).catch(e => setErr(e.message));
  }, []);

  // The gallery iframe reports its content height; grow the iframe to fit so
  // the admin page is the single scroll instead of a nested iframe scrollbar.
  React.useEffect(() => {
    const onMsg = e => {
      if (e.data && e.data.type === 'style-gallery-height' && galleryRef.current && e.data.height) {
        galleryRef.current.style.height = e.data.height + 'px';
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // The firm's agents (same set the Agents admin tab manages); skip personal
  // agents (users own those) and manually-uploaded images.
  const targets = React.useMemo(() => (agents || []).filter(a =>
    ((a.kind === 'custom' && a.scope === 'firm') || a.kind === 'system' || a.kind === 'master')
    && a.imageSource !== 'upload',
  ), [agents]);
  const uploadCount = React.useMemo(() => (agents || []).filter(a =>
    ((a.kind === 'custom' && a.scope === 'firm') || a.kind === 'system' || a.kind === 'master')
    && a.imageSource === 'upload',
  ).length, [agents]);

  const regenAll = async () => {
    if (!targets.length) return;
    const msg = `Regenerate images for ${targets.length} agent${targets.length === 1 ? '' : 's'} in the current style?\n\n`
      + `This makes ${targets.length} paid gpt-image-1 call${targets.length === 1 ? '' : 's'} (one per agent), runs one at a time, and may take a few minutes. `
      + (uploadCount ? `${uploadCount} agent${uploadCount === 1 ? '' : 's'} with a manually-uploaded image will be skipped. ` : '')
      + `Keep this tab open.`;
    if (!window.confirm(msg)) return;
    setRunning(true); setErr(null); abortRef.current = false;
    let ok = 0;
    const fails = [];   // [{ label, msg }] — every failure, surfaced live
    for (let i = 0; i < targets.length; i++) {
      if (abortRef.current) break;
      const a = targets[i];
      setProg({ done: i, total: targets.length, current: a.targetLabel, ok, fails: [...fails] });
      try { await regenerateAgentImage(a.id); ok++; }
      catch (e) { fails.push({ label: a.targetLabel, msg: e.message || String(e) }); }
      setProg({ done: i + 1, total: targets.length, current: a.targetLabel, ok, fails: [...fails] });
    }
    setRunning(false);
    setProg(p => p ? { ...p, current: null } : p);
    // Refresh so the iframe gallery + counts reflect the new images.
    listAgents().then(r => setAgents(r.agents || [])).catch(() => {});
  };

  const pct = prog && prog.total ? Math.round((prog.done / prog.total) * 100) : 0;

  return (
    <div>
      <div className="lede" style={{marginBottom:16}}>
        The art style for every agent's hero image. Click a style below to make it the firm-wide standard —
        new and regenerated agent images use it. Changing the standard does <strong>not</strong> re-skin existing
        agents on its own; use <em>Regenerate all</em> to apply it across the firm.
      </div>

      <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:14,padding:'12px 14px',border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-alt)'}}>
        <button
          type="button"
          className="btn-orange"
          style={{padding:'8px 14px',fontSize:13}}
          disabled={running || agents === null || targets.length === 0}
          onClick={regenAll}
        >
          {running ? 'Regenerating…' : `↻ Regenerate all agent images (${agents === null ? '…' : targets.length})`}
        </button>
        {running && <button type="button" className="btn-ghost" style={{padding:'8px 14px',fontSize:13}} onClick={() => { abortRef.current = true; }}>Stop</button>}
        <span style={{fontSize:12.5,color:'var(--fg-muted)'}}>
          Applies the current standard to every firm agent — one paid image each.{uploadCount ? ` Skips ${uploadCount} uploaded image${uploadCount === 1 ? '' : 's'}.` : ''}
        </span>
      </div>

      {prog && (
        <div style={{marginBottom:14,fontSize:13}}>
          <div style={{height:8,background:'var(--bg-alt)',borderRadius:5,overflow:'hidden',marginBottom:6}}>
            <div style={{height:'100%',width:`${pct}%`,background:'#FF6428',transition:'width .2s'}}/>
          </div>
          <span style={{color:'var(--fg-muted)'}}>
            {prog.done}/{prog.total} · {prog.ok} done{prog.fails.length ? `, ${prog.fails.length} failed` : ''}
            {running && prog.current ? ` · now: ${prog.current}` : (!running ? ' · finished' : '')}
          </span>
          {prog.fails.length > 0 && (
            <div style={{color:'#A20235',marginTop:6,fontSize:12.5}}>
              <div style={{fontWeight:600,marginBottom:2}}>Failed{running ? ' so far' : ''} ({prog.fails.length}):</div>
              <ul style={{margin:'2px 0 0',paddingLeft:18,lineHeight:1.5}}>
                {prog.fails.map((f, i) => <li key={i}><strong>{f.label}</strong> — {f.msg}</li>)}
              </ul>
              {!running && <div style={{marginTop:4,color:'var(--fg-muted)'}}>Fix the cause, then regenerate the failed agent(s) — open each one and click ↻ Regenerate (no need to re-run all).</div>}
            </div>
          )}
        </div>
      )}
      {err && <div style={{padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5,marginBottom:12}}>{err}</div>}

      <iframe
        ref={galleryRef}
        title="Agent image styles"
        src="/api/style-gallery"
        scrolling="no"
        onLoad={() => {
          // Same-origin: read the content height directly as a fallback in case
          // the gallery's postMessage is missed, so content is never clipped.
          try {
            const h = galleryRef.current?.contentWindow?.document?.body?.scrollHeight;
            if (h) galleryRef.current.style.height = h + 'px';
          } catch { /* cross-origin — rely on postMessage */ }
        }}
        style={{display:'block',width:'100%',height:480,border:'1px solid var(--border)',borderRadius:10,background:'#fff',overflow:'hidden'}}
      />
    </div>
  );
};

const AdminSettings = () => {
  const [settings, setSettings] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [savingKey, setSavingKey] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    // agent-image-style is managed via the visual "Agent images" tab, so it's
    // hidden from this generic dropdown list.
    loadAdminSettings()
      .then(d => setSettings((d.settings || []).filter(s => s.key !== 'agent-image-style')))
      .catch(e => setError(e.message));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const onChange = async (key, value) => {
    setSavingKey(key);
    setError(null);
    try {
      await saveAdminSetting(key, value);
      setSettings(s => s.map(x => x.key === key ? { ...x, value } : x));
    } catch (e) {
      setError(e.message);
      reload();
    } finally {
      setSavingKey(null);
    }
  };

  if (error && !settings) return <div style={{padding:12,marginBottom:18,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>Settings load failed: {error}</div>;
  if (!settings) return <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>Loading settings…</div>;

  return (
    <div>
      <div className="lede" style={{marginBottom:16}}>Operational toggles for the firm's pipelines. Changes take effect on the next run.</div>
      {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>Save failed: {error}</div>}
      {settings.map(s => (
        <div key={s.key} style={{marginBottom:18,padding:14,border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-alt)'}}>
          <div style={{fontSize:14,fontWeight:600}}>{s.label}</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',margin:'4px 0 10px',lineHeight:1.5}}>{s.description}</div>
          <select
            className="input"
            style={{maxWidth:300}}
            value={s.value}
            disabled={savingKey === s.key}
            onChange={e => onChange(s.key, e.target.value)}
          >
            {s.options.map(o => <option key={o} value={o}>{o}{o === s.default ? ' (default)' : ''}</option>)}
          </select>
          {savingKey === s.key && <span style={{marginLeft:10,fontSize:12,color:'var(--fg-muted)'}}>Saving…</span>}
        </div>
      ))}
    </div>
  );
};
/* ─── Budget panel (per-user + global daily caps) ────────────── */

async function loadBudget() {
  const res = await fetch('/api/admin/ai-budget', { credentials: 'same-origin' });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `budget → ${res.status}`);
  return j;
}
async function saveBudget(patch) {
  const res = await fetch('/api/admin/ai-budget', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `budget → ${res.status}`);
  return j;
}

const BudgetPanel = () => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [editing, setEditing] = React.useState(null);  // { perUserDailyUsd, globalDailyUsd } | null
  const [saving, setSaving] = React.useState(false);

  const reload = React.useCallback(() => {
    setError(null);
    loadBudget().then(setData).catch(e => setError(e.message));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  if (error) return <div style={{padding:12,marginBottom:18,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>Budget load failed: {error}</div>;
  if (!data) return <div style={{padding:12,marginBottom:18,color:'var(--fg-muted)',fontSize:13}}>Loading budget…</div>;

  const userPercent = Math.min(100, Math.round((data.todayGlobalUsd / Math.max(data.limits.globalDailyUsd, 0.0001)) * 100));
  const pctColor =
    userPercent >= 95 ? '#A20235' :
    userPercent >= 80 ? '#B83C0F' :
                        '#00702A';

  const startEdit = () => setEditing({
    perUserDailyUsd: data.limits.perUserDailyUsd,
    globalDailyUsd:  data.limits.globalDailyUsd,
  });
  const commit = async () => {
    setSaving(true);
    try { await saveBudget(editing); setEditing(null); reload(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{marginBottom:24,padding:14,border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-alt)'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
        <div>
          <div style={{fontSize:14,fontWeight:600}}>Daily AI budget</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>
            Global firm cap blocks all AI calls when reached. Per-user cap blocks just that user. Counts Claude (Anthropic) + OpenAI (TTS, Whisper, Realtime).
          </div>
        </div>
        {!editing && (
          <button type="button" onClick={startEdit} className="btn-ghost" style={{padding:'6px 12px',fontSize:13}}>Edit limits</button>
        )}
      </div>

      {!editing ? (
        <div className="respo-3col" style={{gap:12}}>
          <div className="kpi">
            <div className="kpi__label">Today firm-wide</div>
            <div className="kpi__value" style={{color:pctColor}}>${data.todayGlobalUsd.toFixed(2)}</div>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2}}>of ${data.limits.globalDailyUsd.toFixed(2)} cap · {userPercent}%</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">Per-user cap</div>
            <div className="kpi__value">${data.limits.perUserDailyUsd.toFixed(2)}</div>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2}}>per user per UTC day</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">Top spender today</div>
            <div className="kpi__value" style={{fontSize:18}}>
              {data.perUser?.[0] ? `${data.perUser[0].userName || data.perUser[0].userId.slice(0, 8)}` : '—'}
            </div>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2}}>
              {data.perUser?.[0] ? `$${data.perUser[0].todayUsd.toFixed(2)} today` : 'no spend yet'}
            </div>
          </div>
        </div>
      ) : (
        <div style={{display:'flex',gap:12,alignItems:'flex-end',flexWrap:'wrap'}}>
          <label style={{flex:1,minWidth:160}}>
            <div style={{fontSize:11,color:'var(--fg-muted)',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.05em'}}>Per user / day (USD)</div>
            <input
              type="number" min="0" step="1" className="input"
              value={editing.perUserDailyUsd}
              onChange={e => setEditing(s => ({ ...s, perUserDailyUsd: Number(e.target.value) }))}
            />
          </label>
          <label style={{flex:1,minWidth:160}}>
            <div style={{fontSize:11,color:'var(--fg-muted)',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.05em'}}>Global firm / day (USD)</div>
            <input
              type="number" min="0" step="1" className="input"
              value={editing.globalDailyUsd}
              onChange={e => setEditing(s => ({ ...s, globalDailyUsd: Number(e.target.value) }))}
            />
          </label>
          <button type="button" onClick={commit} className="btn-orange" disabled={saving} style={{padding:'9px 16px'}}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => setEditing(null)} className="btn-ghost" disabled={saving} style={{padding:'9px 16px'}}>
            Cancel
          </button>
        </div>
      )}

      {data.perUser && data.perUser.length > 0 && !editing && (
        <details style={{marginTop:12}}>
          <summary style={{cursor:'pointer',fontSize:12.5,color:'var(--fg-muted)'}}>{data.perUser.length} user{data.perUser.length === 1 ? '' : 's'} with spend today — show breakdown</summary>
          <table style={{width:'100%',marginTop:10,borderCollapse:'collapse',fontSize:12.5}}>
            <thead><tr style={{textAlign:'left',color:'var(--fg-muted)'}}><th style={{padding:'4px 0'}}>User</th><th style={{padding:'4px 0',textAlign:'right'}}>Today</th><th style={{padding:'4px 0',textAlign:'right'}}>7 days</th><th style={{padding:'4px 0',textAlign:'right'}}>30 days</th></tr></thead>
            <tbody>
              {data.perUser.map(u => (
                <tr key={u.userId} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={{padding:'4px 0'}}>{u.userName || u.userId.slice(0, 12)}</td>
                  <td style={{padding:'4px 0',textAlign:'right',color: u.todayUsd >= data.limits.perUserDailyUsd ? '#A20235' : 'inherit'}}>${u.todayUsd.toFixed(2)}</td>
                  <td style={{padding:'4px 0',textAlign:'right'}}>${u.weekUsd.toFixed(2)}</td>
                  <td style={{padding:'4px 0',textAlign:'right'}}>${u.monthUsd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
};

async function saveAiModelOverride(targetId, patch) {
  const res = await fetch('/api/admin/ai-models', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ targetId, ...patch }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `ai-models patch → ${res.status}`);
  return j;
}
const fmtTokens = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};
const fmtUsd = (n) => n >= 100 ? `$${n.toFixed(0)}` : n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(3)}`;

const AdminAiModels = () => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [savingId, setSavingId] = React.useState(null);
  const [filter, setFilter] = React.useState('');
  const reload = React.useCallback(async () => {
    setError(null);
    try { setData(await loadAiModels()); }
    catch (err) { setError(err.message); }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const onChange = async (targetId, patch) => {
    setSavingId(targetId);
    try {
      await saveAiModelOverride(targetId, patch);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  if (error) return <div style={{padding:14,background:'#FCE6E0',color:'#A20235',borderRadius:8}}>AI Models load failed: {error}</div>;
  if (!data) return <div style={{padding:14,color:'var(--fg-muted)'}}>Loading…</div>;

  const sites = data.sites || [];
  const models = data.models || [];
  const filtered = !filter ? sites : sites.filter(s =>
    s.label.toLowerCase().includes(filter.toLowerCase()) ||
    s.id.toLowerCase().includes(filter.toLowerCase()) ||
    s.category.toLowerCase().includes(filter.toLowerCase()),
  );

  // Group by category for display.
  const byCategory = new Map();
  for (const s of filtered) {
    if (!byCategory.has(s.category)) byCategory.set(s.category, []);
    byCategory.get(s.category).push(s);
  }
  const categoryOrder = ['Slide Deck Creator','Proposal Builder','Memo','Chat','Planner','Master Agents','Admin Tools','Other'];
  const categories = Array.from(byCategory.keys()).sort((a, b) => {
    const ai = categoryOrder.indexOf(a), bi = categoryOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  // Totals across all sites for the summary chips.
  // Headline token counts include cache read/write tokens so the totals
  // match what the Anthropic console reports (cache reads usually dominate
  // when prompt caching is on). Cost is already computed server-side with
  // cache billing, so it stays as-is.
  const totals = sites.reduce((acc, s) => ({
    tokens24:  acc.tokens24  + (s.usage.d1.tokensIn  + s.usage.d1.tokensOut  + s.usage.d1.cacheCreate  + s.usage.d1.cacheRead),
    tokens7:   acc.tokens7   + (s.usage.d7.tokensIn  + s.usage.d7.tokensOut  + s.usage.d7.cacheCreate  + s.usage.d7.cacheRead),
    tokens30:  acc.tokens30  + (s.usage.d30.tokensIn + s.usage.d30.tokensOut + s.usage.d30.cacheCreate + s.usage.d30.cacheRead),
    cost24:    acc.cost24    + s.usage.d1.costUsd,
    cost7:     acc.cost7     + s.usage.d7.costUsd,
    cost30:    acc.cost30    + s.usage.d30.costUsd,
  }), { tokens24:0, tokens7:0, tokens30:0, cost24:0, cost7:0, cost30:0 });

  return (
    <div>
      <div style={{marginBottom:18}}>
        <h2 style={{margin:'0 0 4px',fontSize:22}}>AI Models</h2>
        <div style={{fontSize:13.5,color:'var(--fg-muted)',lineHeight:1.5}}>
          Every Claude call site in the system. Set a model or max-token override per site without a deploy; usage + cost aggregates over the last 24h / 7d / 30d give visibility into which calls are spending what.
        </div>
      </div>

      <BudgetPanel/>

      <div className="respo-3col" style={{gap:12,marginBottom:18}}>
        <div className="kpi">
          <div className="kpi__label">Last 24h</div>
          <div className="kpi__value">{fmtTokens(totals.tokens24)} tokens</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>{fmtUsd(totals.cost24)} est.</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Last 7 days</div>
          <div className="kpi__value">{fmtTokens(totals.tokens7)} tokens</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>{fmtUsd(totals.cost7)} est.</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Last 30 days</div>
          <div className="kpi__value">{fmtTokens(totals.tokens30)} tokens</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>{fmtUsd(totals.cost30)} est.</div>
        </div>
      </div>

      <div style={{marginBottom:14}}>
        <input
          className="input"
          placeholder="Filter call sites (name / id / category)"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{width:'100%',maxWidth:480}}
        />
      </div>

      {categories.map(cat => (
        <div key={cat} style={{marginBottom:24}}>
          <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:8}}>{cat}</div>
          <div className="scroll-x-mobile">
            <table className="tbl" style={{width:'100%'}}>
              <thead>
                <tr>
                  <th>Call site</th>
                  <th style={{width:200}}>Model</th>
                  <th style={{width:110}}>Max tokens</th>
                  <th style={{width:110}}>24h</th>
                  <th style={{width:110}}>7d</th>
                  <th style={{width:110}}>30d</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.get(cat).map(s => (
                  <AiSiteRow
                    key={s.id}
                    site={s}
                    models={models}
                    saving={savingId === s.id}
                    onChange={onChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const AiSiteRow = ({ site, models, saving, onChange }) => {
  const [maxTokensDraft, setMaxTokensDraft] = React.useState(String(site.effectiveMaxTokens));
  React.useEffect(() => { setMaxTokensDraft(String(site.effectiveMaxTokens)); }, [site.effectiveMaxTokens]);

  const overrideActive = !!site.override;
  const modelChanged   = site.effectiveModel     !== site.defaultModel;
  const maxChanged     = site.effectiveMaxTokens !== site.defaultMaxTokens;

  const commitMaxTokens = () => {
    const n = parseInt(maxTokensDraft, 10);
    if (!Number.isFinite(n) || n < 100) {
      setMaxTokensDraft(String(site.effectiveMaxTokens));
      return;
    }
    if (n === site.effectiveMaxTokens) return;
    onChange(site.id, { maxTokens: n });
  };

  return (
    <tr>
      <td style={{verticalAlign:'top'}}>
        <div style={{fontWeight:500,fontSize:13.5}}>
          {site.label}
          {overrideActive && (
            <span style={{marginLeft:8,fontSize:10.5,padding:'1px 7px',background:'#FFE7DC',color:'#B83C0F',borderRadius:99,letterSpacing:'0.04em',textTransform:'uppercase',fontWeight:600}}>OVERRIDE</span>
          )}
        </div>
        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,lineHeight:1.4}}>{site.description}</div>
        <div style={{fontSize:10.5,color:'var(--fg-muted)',marginTop:3,fontFamily:'JetBrains Mono, Menlo, monospace'}}>{site.id}</div>
      </td>
      <td style={{verticalAlign:'top'}}>
        <select
          className="select"
          value={site.effectiveModel}
          onChange={e => onChange(site.id, { model: e.target.value })}
          disabled={saving}
          style={{fontSize:12.5,padding:'6px 8px',width:'100%',background: modelChanged ? '#FFE7DC' : undefined}}
        >
          {models.map(m => (
            <option key={m.id} value={m.id}>{m.label}{m.id === site.defaultModel ? ' (default)' : ''}</option>
          ))}
        </select>
      </td>
      <td style={{verticalAlign:'top'}}>
        <input
          className="input"
          type="number"
          min="100"
          max="128000"
          step="100"
          value={maxTokensDraft}
          onChange={e => setMaxTokensDraft(e.target.value)}
          onBlur={commitMaxTokens}
          onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
          disabled={saving}
          style={{fontSize:12.5,padding:'6px 8px',width:'100%',background: maxChanged ? '#FFE7DC' : undefined,fontFamily:'JetBrains Mono, Menlo, monospace'}}
        />
        <div style={{fontSize:10.5,color:'var(--fg-muted)',marginTop:2}}>default {site.defaultMaxTokens.toLocaleString()}</div>
      </td>
      <AiUsageCell w={site.usage.d1}/>
      <AiUsageCell w={site.usage.d7}/>
      <AiUsageCell w={site.usage.d30}/>
    </tr>
  );
};

const AiUsageCell = ({ w }) => {
  const total = w ? (w.tokensIn + w.tokensOut + (w.cacheCreate || 0) + (w.cacheRead || 0)) : 0;
  if (!total) {
    return <td style={{verticalAlign:'top',fontSize:12,color:'var(--fg-muted)'}}>—</td>;
  }
  return (
    <td style={{verticalAlign:'top',fontSize:12,lineHeight:1.5}}>
      <div style={{fontWeight:500}}>{fmtTokens(total)}</div>
      <div style={{color:'var(--fg-muted)',fontSize:11}}>{fmtUsd(w.costUsd)}</div>
      <div style={{color:'var(--fg-muted)',fontSize:11}}>{w.calls} call{w.calls === 1 ? '' : 's'}</div>
    </td>
  );
};
export const AdminReadmeView = OnboardingView;
